import React, { useEffect, useRef, useState } from "react";
import { Navigate, useParams, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Building2, Users, MessageSquare, Eye, CheckCircle, Trash2, Star, TrendingUp, Target,
  IndianRupee, Edit, Clock, Settings2, Save, FileText, Download, Tag, Handshake, Plus,
  FileSpreadsheet, BookOpen, Mail, UserPlus, BookMarked, ArrowDownCircle, ArrowUpCircle,
  Wallet, Calendar, ArrowLeft, Send, MessageCircle, CheckCircle2, AlertCircle,
  Activity, Smartphone, Monitor, Globe, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { BarChart, LineChart } from "@/components/site/Charts";
import { LOCALITIES, PROPERTY_TYPES } from "@/data/constants";

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

const Stat = ({ icon: I, label, value, color = "kp-teal", onClick, active }) => (
  <button type="button" onClick={onClick} className={`text-left bg-white border rounded-2xl p-5 transition-all ${active ? "border-kp-teal ring-2 ring-kp-teal/20" : "border-slate-200 hover:border-kp-teal hover:-translate-y-0.5"} ${onClick ? "cursor-pointer" : "cursor-default"}`}>
    <div className={`w-11 h-11 rounded-lg flex items-center justify-center mb-3 ${color === "kp-teal" ? "bg-kp-teal-light text-kp-teal" : "bg-kp-maroon-light text-kp-maroon"}`}><I size={20}/></div>
    <div className="font-display text-2xl font-bold text-slate-900">{typeof value === "number" ? value.toLocaleString("en-IN") : value}</div>
    <div className="text-xs text-slate-500">{label}</div>
  </button>
);

/** Premium hero tile for Account Book — emerald ledger aesthetic with today's income live */
const AccountBookHero = ({ todayIncome = 0, revenue6m = 0, onClick }) => (
  <button type="button" onClick={onClick} data-testid="admin-hero-ledger"
    className="group relative overflow-hidden text-left rounded-3xl p-6 md:p-7 border border-emerald-200/60 bg-gradient-to-br from-emerald-50 via-white to-teal-50 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-emerald-100 transition-all">
    <div aria-hidden className="absolute inset-0 opacity-[0.06] pointer-events-none"
      style={{ backgroundImage: "linear-gradient(#065f46 1px,transparent 1px),linear-gradient(90deg,#065f46 1px,transparent 1px)", backgroundSize: "22px 22px" }}/>
    <div className="relative flex items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-emerald-700 bg-emerald-100/70 px-2 py-1 rounded-full">
          <BookMarked size={11}/> Today · Live
        </div>
        <div className="font-display text-3xl md:text-4xl font-black text-emerald-900 mt-3 tabular-nums" data-testid="hero-today-income">{inr(todayIncome)}</div>
        <div className="text-sm text-emerald-900/70 mt-1">Income booked today · Auto-posted on deal close</div>
        <div className="text-[11px] text-emerald-800/70 mt-2 flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1"><ArrowDownCircle size={12}/> 6-mo revenue <b className="text-emerald-900">{inr(revenue6m)}</b></span>
          <span className="inline-flex items-center gap-1"><Wallet size={12}/> Paid / Unpaid tracking</span>
        </div>
      </div>
      <div className="shrink-0 w-16 h-16 rounded-2xl bg-emerald-600 text-white flex items-center justify-center shadow-lg shadow-emerald-600/20 rotate-[-4deg] group-hover:rotate-0 transition-transform">
        <Wallet size={30}/>
      </div>
    </div>
    <div className="relative mt-5 flex items-center justify-between">
      <span className="text-xs font-semibold text-emerald-800/80">Open Account Book →</span>
      <div className="flex items-end gap-1 h-8">
        {[38, 60, 44, 72, 55, 88, 62].map((h, i) => (
          <span key={i} className="w-1.5 rounded-full bg-emerald-500/70 group-hover:bg-emerald-600 transition-colors" style={{ height: `${h}%` }}/>
        ))}
      </div>
    </div>
  </button>
);

/** Premium hero tile for CRM — maroon pipeline aesthetic with this-week live delta */
const CRMHero = ({ totalLeads = 0, weekNewLeads = 0, onClick }) => {
  const stages = ["New", "Contacted", "Visit", "Nego.", "Won"];
  return (
    <button type="button" onClick={onClick} data-testid="admin-hero-crm"
      className="group relative overflow-hidden text-left rounded-3xl p-6 md:p-7 border border-kp-maroon/25 bg-gradient-to-br from-[#3a0a1a] via-[#5a1029] to-[#800020] text-white hover:-translate-y-0.5 hover:shadow-xl hover:shadow-kp-maroon/25 transition-all">
      <div aria-hidden className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5 blur-2xl"/>
      <div aria-hidden className="absolute -bottom-20 -left-10 w-56 h-56 rounded-full bg-white/[0.04] blur-2xl"/>
      <div className="relative flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-amber-200/90 bg-white/10 border border-white/10 px-2 py-1 rounded-full">
            <UserPlus size={11}/> CRM Pipeline
          </div>
          <div className="font-display text-3xl md:text-4xl font-black text-white mt-3 tabular-nums flex items-baseline gap-2" data-testid="hero-total-leads">
            {totalLeads.toLocaleString("en-IN")} <span className="text-lg font-semibold text-white/60">leads</span>
            {weekNewLeads > 0 && (
              <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-emerald-300 bg-emerald-500/15 border border-emerald-400/30 px-2 py-0.5 rounded-full" data-testid="hero-week-new-leads">
                <TrendingUp size={11}/> +{weekNewLeads} this week
              </span>
            )}
          </div>
          <div className="text-sm text-white/70 mt-1">Every enquiry, follow-up &amp; win in one board</div>
        </div>
        <div className="shrink-0 w-16 h-16 rounded-2xl bg-white/10 border border-white/15 backdrop-blur-sm flex items-center justify-center rotate-[4deg] group-hover:rotate-0 transition-transform">
          <Target size={30} className="text-amber-200"/>
        </div>
      </div>
      <div className="relative mt-5 flex flex-wrap items-center gap-1.5">
        {stages.map((s, i) => (
          <span key={s} className="text-[10px] font-semibold uppercase tracking-wide text-white/90 bg-white/10 border border-white/10 px-2 py-1 rounded-full">
            {s}{i < stages.length - 1 && <span className="text-white/40 ml-1.5">›</span>}
          </span>
        ))}
      </div>
      <div className="relative mt-4 flex items-center justify-between">
        <span className="text-xs font-semibold text-amber-200/90">Open Pipeline · Drag &amp; drop →</span>
        <span className="inline-flex items-center gap-1 text-[11px] text-white/70"><Handshake size={12}/> Promote enquiries in one click</span>
      </div>
    </button>
  );
};

function ExportButton({ kind }) {
  const download = async (fmt) => {
    try {
      const res = await api.get(`/admin/export/${kind}`, { params: { fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const date = new Date().toISOString().slice(0, 10);
      a.download = `krishna-property-${kind}-${date}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind} exported as ${fmt.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className="rounded-full gap-1.5 text-xs" data-testid={`export-${kind}-btn`}>
          <Download size={13}/> Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => download("xlsx")} data-testid={`export-${kind}-xlsx`}>
          <FileSpreadsheet size={14} className="mr-2"/> Excel (.xlsx)
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => download("pdf")} data-testid={`export-${kind}-pdf`}>
          <FileText size={14} className="mr-2"/> PDF (.pdf)
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const SECTION_TITLES = {
  properties: "All Properties",
  pending: "Pending Requests",
  users: "Registered Users",
  enquiries: "Customer Enquiries",
  invoices: "GST Invoices",
  crm: "CRM — Leads Pipeline",
  ledger: "Account Book — Income & Expense",
  promotions: "Locality Promotions",
  blog: "Blog CMS",
  broadcast: "WhatsApp Broadcast",
  settings: "Settings",
};

export default function Admin() {
  const { user, loading } = useAuth();
  const { section } = useParams();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [props, setProps] = useState([]);
  const [users, setUsers] = useState([]);
  const [enq, setEnq] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [editing, setEditing] = useState(null);
  const [closing, setClosing] = useState(null);
  const [promoCount, setPromoCount] = useState(0);
  const [leadCount, setLeadCount] = useState(0);
  const [blogCount, setBlogCount] = useState(0);
  const tabsRef = useRef(null);
  const tab = section || null;
  const goTab = (t) => nav(`/admin/${t}`);

  const load = () => {
    api.get("/admin/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/admin/analytics").then(r => setAnalytics(r.data)).catch(() => {});
    api.get("/admin/properties").then(r => setProps(r.data)).catch(() => {});
    api.get("/admin/users").then(r => setUsers(r.data)).catch(() => {});
    api.get("/admin/enquiries").then(r => setEnq(r.data)).catch(() => {});
    api.get("/admin/invoices").then(r => setInvoices(r.data)).catch(() => {});
    api.get("/admin/promotions").then(r => setPromoCount(r.data.length)).catch(() => {});
    api.get("/admin/leads").then(r => setLeadCount(r.data.length)).catch(() => {});
    api.get("/admin/blog").then(r => setBlogCount(r.data.length)).catch(() => {});
  };
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  const verify = async (id) => { await api.post(`/admin/properties/${id}/verify`); toast.success("Verified"); load(); };
  const feature = async (id) => { await api.post(`/admin/properties/${id}/feature`); toast.success("Toggled featured"); load(); };
  const del = async (id) => { if (!window.confirm("Delete this property?")) return; await api.delete(`/admin/properties/${id}`); toast.success("Deleted"); load(); };

  const downloadInvoice = async (inv) => {
    try {
      const res = await api.get(`/admin/invoices/${inv.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${inv.invoice_no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download invoice"); }
  };

  if (loading) return <div className="p-10 text-center">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace/>;

  const totalRevenue = invoices.reduce((s, i) => s + (i.total || 0), 0);

  return (
    <div className="w-full mx-auto px-3 md:px-4 py-6" data-testid="admin-panel">
      {tab ? (
        <div className="mb-6 flex items-center gap-3">
          <Link to="/admin" className="w-10 h-10 rounded-full bg-slate-100 hover:bg-kp-teal-light hover:text-kp-teal flex items-center justify-center transition-colors" data-testid="admin-back" title="Back to Dashboard"><ArrowLeft size={18}/></Link>
          <div>
            <div className="text-xs text-slate-500 flex items-center gap-1"><Link to="/admin" className="hover:text-kp-teal">Dashboard</Link> <span>/</span> <span className="text-slate-400">{tab}</span></div>
            <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">{SECTION_TITLES[tab] || tab}</h1>
          </div>
        </div>
      ) : (
        <>
          <h1 className="font-display text-3xl font-bold text-slate-900">Admin Dashboard</h1>
          <p className="text-slate-500 mt-1">Manage properties, users, enquiries, invoices and promotions for Krishna Property.</p>
        </>
      )}

      {!tab && (
      <>
        {/* Row 1 — Core inventory & customers */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat icon={Plus} label="Post New Property" value={"+"} color="kp-maroon" onClick={() => nav("/post-property")}/>
          <Stat icon={Building2} label="Total Properties" value={stats?.total_properties || 0} onClick={() => goTab("properties")} active={tab === "properties"}/>
          <Stat icon={Clock} label="Pending" value={stats?.pending_properties || 0} color="kp-maroon" onClick={() => goTab("pending")} active={tab === "pending"}/>
          <Stat icon={Users} label="Users" value={stats?.total_users || 0} onClick={() => goTab("users")} active={tab === "users"}/>
          <Stat icon={MessageSquare} label="Enquiries" value={stats?.total_enquiries || 0} onClick={() => goTab("enquiries")} active={tab === "enquiries"}/>
        </div>

        {/* Row 2 — Revenue, growth & outreach (Blog + Broadcast moved here) */}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-4">
          <Stat icon={FileText} label="Invoices" value={invoices.length} color="kp-maroon" onClick={() => goTab("invoices")} active={tab === "invoices"}/>
          <Stat icon={IndianRupee} label="Billed Revenue" value={inr(totalRevenue)} onClick={() => goTab("invoices")} active={tab === "invoices"}/>
          <Stat icon={Tag} label="Promotions" value={promoCount} color="kp-maroon" onClick={() => goTab("promotions")} active={tab === "promotions"}/>
          <Stat icon={BookOpen} label="Blog" value={blogCount} onClick={() => goTab("blog")} active={tab === "blog"}/>
          <Stat icon={Send} label="Broadcast" value={"WA"} color="kp-maroon" onClick={() => goTab("broadcast")} active={tab === "broadcast"}/>
        </div>

        {/* Row 3 — Hero tiles: Account Book + CRM (distinct premium designs) */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <AccountBookHero
            todayIncome={stats?.today_income || 0}
            revenue6m={analytics?.totals?.revenue_6m || 0}
            onClick={() => goTab("ledger")}
          />
          <CRMHero
            totalLeads={stats?.total_leads ?? leadCount}
            weekNewLeads={stats?.week_new_leads || 0}
            onClick={() => goTab("crm")}
          />
        </div>
      </>
      )}

      {!tab && (
      <div className="mt-4 flex justify-end gap-2 text-xs text-slate-500">
        <button onClick={() => goTab("settings")} className={`flex items-center gap-1 px-3 py-1.5 rounded-full border ${tab === "settings" ? "border-kp-teal text-kp-teal bg-kp-teal-light" : "border-slate-200 hover:border-kp-teal"}`} data-testid="admin-open-settings"><Settings2 size={12}/> Settings</button>
      </div>
      )}

      {/* Analytics moved below */}

      {tab && (
      <div ref={tabsRef} className="mt-2 bg-white border border-slate-200 rounded-2xl overflow-hidden scroll-mt-20">
        {(tab === "properties" || tab === "pending") && (
          <PropertiesTable
            list={tab === "pending" ? props.filter(p => p.status === "pending" || !p.verified) : props}
            tab={tab}
            onVerify={verify}
            onFeature={feature}
            onDelete={del}
            onEdit={setEditing}
            onClose={setClosing}
          />
        )}

        {tab === "users" && (
          <>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
              <div className="text-sm text-slate-600">Registered users on Krishna Property</div>
              <ExportButton kind="users"/>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-600 uppercase"><tr><th className="p-2">Name</th><th className="p-2">Phone</th><th className="p-2">Email</th><th className="p-2">Role</th><th className="p-2">Joined</th></tr></thead>
              <tbody>{users.map((u) => (
                <tr key={u.id} className="border-t"><td className="p-2">{u.name}</td><td className="p-2">{u.phone}</td><td className="p-2">{u.email || "—"}</td><td className="p-3 capitalize">{u.role}</td><td className="p-2">{new Date(u.created_at).toLocaleDateString()}</td></tr>
              ))}</tbody>
            </table>
          </>
        )}

        {tab === "enquiries" && (
          <>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
              <div className="text-sm text-slate-600">All customer enquiries · <span className="text-slate-400">Promote any enquiry to a CRM lead in one click</span></div>
              <ExportButton kind="enquiries"/>
            </div>
            <div className="p-4 space-y-3">
              {enq.map((e) => {
                const promote = async () => {
                  try {
                    const r = await api.post(`/admin/enquiries/${e.id}/promote`);
                    if (r.data.already_promoted) toast.info("Already promoted to a lead");
                    else toast.success(`Promoted "${e.name}" to CRM lead`);
                    load();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || "Promote failed");
                  }
                };
                return (
                  <div key={e.id} className="border rounded-lg p-3 flex gap-3" data-testid={`enquiry-row-${e.id}`}>
                    {e.property_image && <img src={e.property_image} alt="" className="w-20 h-20 rounded-lg object-cover flex-shrink-0"/>}
                    <div className="flex-1 min-w-0">
                      <div className="flex justify-between text-xs text-slate-500 flex-wrap gap-1">
                        <span><b className="text-slate-700">{e.name}</b> · {e.phone}{e.email ? ` · ${e.email}` : ""}</span>
                        <span>{new Date(e.created_at).toLocaleString()}</span>
                      </div>
                      {e.property_title ? (
                        <a href={`/property/${e.property_id}`} target="_blank" rel="noreferrer" className="font-semibold text-sm mt-1 block text-kp-teal hover:underline">🏠 {e.property_title}</a>
                      ) : (
                        <div className="font-semibold text-sm mt-1 text-slate-600">📨 General enquiry (no linked property)</div>
                      )}
                      {e.property_title && (
                        <div className="text-xs text-slate-500 mt-0.5">{e.property_locality} · <span className="text-kp-maroon font-semibold">{e.property_price}</span> · {e.property_purpose}</div>
                      )}
                      <div className="text-sm text-slate-700 mt-1">"{e.message}"</div>
                      <div className="mt-2 flex items-center justify-end gap-2">
                        {e.promoted_lead_id ? (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-1 rounded-full bg-emerald-100 text-emerald-700" data-testid={`enquiry-promoted-${e.id}`}>
                            <CheckCircle2 size={12}/> Promoted to Lead
                          </span>
                        ) : (
                          <Button size="sm" onClick={promote} className="rounded-full h-8 gap-1 bg-kp-teal hover:bg-kp-teal/90 text-white text-xs" data-testid={`enquiry-promote-${e.id}`}>
                            <UserPlus size={13}/> Promote to Lead
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              {enq.length === 0 && <p className="text-slate-500 text-center py-6">No enquiries yet.</p>}
            </div>
          </>
        )}

        {tab === "invoices" && (
          <>
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
              <div className="text-sm text-slate-600">GST invoices auto-generated on closed deals</div>
              <ExportButton kind="invoices"/>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-xs text-slate-600 uppercase">
                  <tr><th className="p-2">Invoice</th><th className="p-2">Property</th><th className="p-2">Purpose</th><th className="p-2">Deal Amount</th><th className="p-2">Fee</th><th className="p-2">GST</th><th className="p-2">Total</th><th className="p-2">Date</th><th className="p-2"></th></tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv.id} className="border-t hover:bg-slate-50">
                      <td className="p-3 font-mono text-xs">{inv.invoice_no}</td>
                      <td className="p-3 max-w-xs truncate"><div className="font-semibold text-slate-800 truncate">{inv.property_title}</div><div className="text-xs text-slate-500">{inv.property_locality}</div></td>
                      <td className="p-3 capitalize">{inv.purpose}</td>
                      <td className="p-2">{inr(inv.deal_amount)}</td>
                      <td className="p-2">{inr(inv.net_fee)}{inv.discount_pct > 0 && <span className="ml-1 text-[10px] bg-emerald-100 text-emerald-700 px-1 rounded">-{inv.discount_pct}%</span>}</td>
                      <td className="p-2">{inr(inv.gst_amount)}</td>
                      <td className="p-3 font-bold text-kp-maroon">{inr(inv.total)}</td>
                      <td className="p-3 text-xs text-slate-500">{new Date(inv.created_at).toLocaleDateString()}</td>
                      <td className="p-2">
                        <button onClick={() => downloadInvoice(inv)} className="text-kp-teal hover:bg-kp-teal-light p-1.5 rounded flex items-center gap-1 text-xs font-semibold" title="Download PDF" data-testid={`invoice-download-${inv.id}`}>
                          <Download size={14}/> PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                  {invoices.length === 0 && (
                    <tr><td colSpan={9} className="p-8 text-center text-slate-500">No invoices yet. Invoices are generated automatically when a deal is closed on a property.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "promotions" && <PromotionsPanel/>}
        {tab === "blog" && <BlogPanel/>}
        {tab === "crm" && <CRMPanel/>}
        {tab === "ledger" && <LedgerPanel/>}
        {tab === "broadcast" && <BroadcastPanel/>}
        {tab === "settings" && <SettingsPanel/>}
      </div>
      )}

      {editing && <EditPropertyDialog property={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
      {closing && <CloseDealDialog property={closing} onClose={() => setClosing(null)} onDone={(inv) => { setClosing(null); load(); if (inv) downloadInvoice(inv); }}/>}

      {/* Analytics — shown only on the /admin dashboard as a quick reference */}
      {!tab && analytics && (
        <div className="mt-8 space-y-4" data-testid="admin-analytics">
          <h2 className="font-display text-lg font-semibold text-slate-900">Analytics Overview</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="w-11 h-11 rounded-lg bg-kp-teal-light text-kp-teal flex items-center justify-center mb-3"><IndianRupee size={20}/></div>
              <div className="font-display text-2xl font-bold text-slate-900">₹{(analytics.totals.revenue_6m/100000).toLocaleString("en-IN", {maximumFractionDigits:1})} L</div>
              <div className="text-xs text-slate-500">Revenue (last 6 months, 2% commission)</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="w-11 h-11 rounded-lg bg-kp-maroon-light text-kp-maroon flex items-center justify-center mb-3"><TrendingUp size={20}/></div>
              <div className="font-display text-2xl font-bold text-slate-900">₹{(analytics.totals.avg_monthly_revenue/100000).toLocaleString("en-IN", {maximumFractionDigits:1})} L</div>
              <div className="text-xs text-slate-500">Avg Monthly Revenue</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="w-11 h-11 rounded-lg bg-kp-teal-light text-kp-teal flex items-center justify-center mb-3"><MessageSquare size={20}/></div>
              <div className="font-display text-2xl font-bold text-slate-900">{analytics.totals.enquiries_6m.toLocaleString("en-IN")}</div>
              <div className="text-xs text-slate-500">Leads (last 6 months)</div>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="w-11 h-11 rounded-lg bg-kp-maroon-light text-kp-maroon flex items-center justify-center mb-3"><Target size={20}/></div>
              <div className="font-display text-2xl font-bold text-slate-900">{analytics.totals.conversion_pct}%</div>
              <div className="text-xs text-slate-500">Lead → Property Conversion</div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-semibold text-slate-900">Monthly Revenue</h3>
                <span className="text-xs text-slate-500">₹ Lakh (2% commission)</span>
              </div>
              <BarChart data={analytics.series.map(s => ({ label: s.label, value: Math.round(s.revenue/100000) }))} color="#0F5E5A" formatValue={(v) => `${v}L`}/>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-display font-semibold text-slate-900">Lead Conversion Rate</h3>
                <span className="text-xs text-slate-500">% enquiries linked to a property</span>
              </div>
              <LineChart data={analytics.series.map(s => ({ label: s.label, value: s.conversion }))} color="#800020"/>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-display font-semibold text-slate-900 mb-2">Monthly Enquiries</h3>
              <BarChart data={analytics.series.map(s => ({ label: s.label, value: s.enquiries }))} color="#800020"/>
            </div>
            <div className="bg-white border border-slate-200 rounded-2xl p-5">
              <h3 className="font-display font-semibold text-slate-900 mb-2">New Listings</h3>
              <BarChart data={analytics.series.map(s => ({ label: s.label, value: s.new_properties }))} color="#0F5E5A"/>
            </div>
          </div>
        </div>
      )}

      {!tab && <VisitorAnalyticsSection/>}
      {!tab && <InterestAnalyticsSection/>}
    </div>
  );
}

function VisitorAnalyticsSection() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const load = () => {
    setBusy(true);
    api.get(`/admin/visitor-analytics?days=${days}`)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setBusy(false));
  };
  useEffect(() => { load(); }, [days]);
  useEffect(() => {
    // Auto-refresh live count every 20s
    const t = setInterval(() => api.get(`/admin/visitor-analytics?days=${days}`).then((r) => setData(r.data)).catch(() => {}), 20000);
    return () => clearInterval(t);
  }, [days]);

  if (!data) return null;
  const desktop = data.devices?.desktop || 0;
  const mobile = data.devices?.mobile || 0;
  const totalDev = Math.max(desktop + mobile, 1);
  const desktopPct = Math.round((desktop / totalDev) * 100);
  const mobilePct = 100 - desktopPct;
  // Cap the chart to ~14 buckets for readability on smaller screens
  const chartSeries = data.series.length > 14
    ? data.series.filter((_, i) => i % Math.ceil(data.series.length / 14) === 0 || i === data.series.length - 1)
    : data.series;
  const topPageMax = Math.max(...(data.top_pages || []).map((p) => p.views), 1);

  return (
    <div className="mt-8 space-y-4" data-testid="admin-visitor-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center"><Activity size={18}/></div>
          <div>
            <h2 className="font-display text-lg font-semibold text-slate-900">Live Visitor Analytics</h2>
            <p className="text-xs text-slate-500">Real-time site traffic, updated every 20 seconds.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5" data-testid="visitor-range-toggle">
            {[7, 14, 30].map((d) => (
              <button key={d} onClick={() => setDays(d)} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${days === d ? "bg-white text-kp-teal shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid={`visitor-range-${d}`}>
                {d}d
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={busy} className="rounded-full gap-1 text-xs" data-testid="visitor-refresh">
            <RefreshCw size={12} className={busy ? "animate-spin" : ""}/> Refresh
          </Button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <div className="relative rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 overflow-hidden" data-testid="visitor-live-card">
          <div className="absolute top-3 right-3 flex items-center gap-1">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"/>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"/>
            </span>
            <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide">Live</span>
          </div>
          <div className="w-9 h-9 rounded-lg bg-emerald-500 text-white flex items-center justify-center mb-2"><Activity size={16}/></div>
          <div className="font-display text-3xl font-black text-emerald-900 tabular-nums" data-testid="visitor-live-now">{data.live_now}</div>
          <div className="text-[11px] text-emerald-800/80 mt-0.5">Active right now</div>
        </div>
        <StatMini icon={Eye} label="Today · Views" value={data.today.views} sub={`${data.today.visitors} visitors`} tone="teal"/>
        <StatMini icon={Users} label="Today · Visitors" value={data.today.visitors} sub={`Yday ${data.yesterday.visitors}`} tone="maroon"/>
        <StatMini icon={TrendingUp} label={`Last 7 days`} value={data.week.views} sub={`${data.week.visitors} visitors`} tone="teal"/>
        <StatMini icon={Globe} label={`Last ${data.days}d · Total`} value={data.total.views} sub={`${data.total.visitors} visitors`} tone="maroon"/>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Daily traffic line chart */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-2xl p-5">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-semibold text-slate-900">Daily Traffic</h3>
            <span className="text-xs text-slate-500">Views over last {data.days} days</span>
          </div>
          {chartSeries.every((s) => s.views === 0) ? (
            <div className="py-12 text-center text-sm text-slate-400">No visits recorded yet. Share your site to start seeing traffic here.</div>
          ) : (
            <LineChart data={chartSeries.map((s) => ({ label: s.label, value: s.views }))} color="#0F5E5A"/>
          )}
        </div>

        {/* Device split donut */}
        <div className="bg-white border border-slate-200 rounded-2xl p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-4">Device Split</h3>
          <div className="flex items-center gap-5">
            <div className="relative w-28 h-28 shrink-0">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#e2e8f0" strokeWidth="3.6"/>
                <circle cx="18" cy="18" r="15.9155" fill="none" stroke="#0F5E5A" strokeWidth="3.6"
                  strokeDasharray={`${desktopPct} ${100 - desktopPct}`} strokeDashoffset="25" strokeLinecap="round"/>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center flex-col">
                <div className="font-display text-xl font-bold text-slate-900 tabular-nums">{desktopPct}%</div>
                <div className="text-[9px] text-slate-500 uppercase tracking-wide">Desktop</div>
              </div>
            </div>
            <div className="space-y-2 flex-1">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-kp-teal"/>
                <Monitor size={13} className="text-slate-500"/>
                <span className="text-xs text-slate-700">Desktop</span>
                <span className="ml-auto text-xs font-bold text-slate-900 tabular-nums">{desktop}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-slate-300"/>
                <Smartphone size={13} className="text-slate-500"/>
                <span className="text-xs text-slate-700">Mobile</span>
                <span className="ml-auto text-xs font-bold text-slate-900 tabular-nums">{mobile}</span>
              </div>
              <div className="pt-2 mt-1 border-t border-slate-100 text-[11px] text-slate-500">
                Mobile share <b className="text-slate-800">{mobilePct}%</b>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Top pages */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-semibold text-slate-900">Top Pages</h3>
          <span className="text-xs text-slate-500">Last {data.days} days</span>
        </div>
        {(data.top_pages || []).length === 0 ? (
          <div className="py-6 text-center text-sm text-slate-400">No pages visited yet.</div>
        ) : (
          <div className="space-y-2">
            {data.top_pages.map((p) => (
              <div key={p.path} className="flex items-center gap-3" data-testid={`visitor-top-page`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-800 truncate">{p.path}</div>
                  <div className="relative h-1.5 bg-slate-100 rounded-full mt-1 overflow-hidden">
                    <div className="absolute inset-y-0 left-0 rounded-full" style={{ width: `${(p.views / topPageMax) * 100}%`, background: "linear-gradient(90deg, var(--kp-teal), #10b981)" }}/>
                  </div>
                </div>
                <div className="text-right tabular-nums shrink-0 w-24">
                  <div className="text-sm font-bold text-slate-900">{p.views.toLocaleString("en-IN")}</div>
                  <div className="text-[10px] text-slate-500">{p.visitors} visitors</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const StatMini = ({ icon: I, label, value, sub, tone = "teal" }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4">
    <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-2 ${tone === "teal" ? "bg-kp-teal-light text-kp-teal" : "bg-kp-maroon-light text-kp-maroon"}`}><I size={16}/></div>
    <div className="font-display text-2xl font-bold text-slate-900 tabular-nums">{typeof value === "number" ? value.toLocaleString("en-IN") : value}</div>
    <div className="text-[11px] text-slate-500">{label}</div>
    {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

function InterestAnalyticsSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/interest-analytics").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return null;
  const rows = (data.rows || []).slice(0, 10);
  const maxTotal = Math.max(...rows.map((r) => r.total_interests), 1);
  const t = data.totals || {};
  return (
    <div className="mt-8 space-y-4" data-testid="admin-interest-analytics">
      <div className="flex items-center gap-2">
        <div className="w-10 h-10 rounded-xl bg-kp-maroon-light text-kp-maroon flex items-center justify-center"><Target size={18}/></div>
        <div>
          <h2 className="font-display text-lg font-semibold text-slate-900">Interest → Deal Conversion</h2>
          <p className="text-xs text-slate-500">Per-locality funnel — spot where interest is strong and where it converts.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatMini icon={MessageSquare} label="Total Interests" value={t.total_interests || 0} tone="teal"/>
        <StatMini icon={Handshake} label="Converted" value={t.converted || 0} sub="Marked as CRM lead" tone="maroon"/>
        <StatMini icon={CheckCircle2} label="Deals Closed" value={t.deals_closed || 0} sub="Sold / signed" tone="teal"/>
        <StatMini icon={TrendingUp} label="Conversion Rate" value={`${t.conversion_pct || 0}%`} sub="Interests → converted" tone="maroon"/>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display font-semibold text-slate-900">Top Localities by Interest Volume</h3>
          <span className="text-xs text-slate-500">{rows.length} localit{rows.length === 1 ? "y" : "ies"}</span>
        </div>
        {rows.length === 0 ? (
          <div className="py-10 text-center text-sm text-slate-400 italic">No interests yet — as buyers &amp; tenants engage, the funnel will appear here.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => {
              const totalPct = Math.round((r.total_interests / maxTotal) * 100);
              const convertedPct = r.total_interests ? Math.round((r.converted / r.total_interests) * 100) : 0;
              const shortlistedPct = r.total_interests ? Math.round((r.shortlisted / r.total_interests) * 100) : 0;
              return (
                <div key={r.locality} className="grid grid-cols-[110px_1fr_auto] items-center gap-3" data-testid={`interest-locality-row-${r.locality}`}>
                  <div className="text-sm font-semibold text-slate-800 truncate" title={r.locality}>{r.locality}</div>
                  <div className="relative">
                    <div className="h-6 rounded-full bg-slate-100 overflow-hidden relative">
                      <div className="absolute inset-y-0 left-0 bg-kp-teal/25" style={{ width: `${totalPct}%` }}/>
                      <div className="absolute inset-y-0 left-0 bg-amber-400/80" style={{ width: `${(shortlistedPct / 100) * totalPct}%` }}/>
                      <div className="absolute inset-y-0 left-0 bg-emerald-500" style={{ width: `${(convertedPct / 100) * totalPct}%` }}/>
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-[10px] text-slate-500 flex-wrap">
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500"/> {r.converted} converted</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-400"/> {r.shortlisted} shortlisted</span>
                      <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-kp-teal/50"/> {r.total_interests} total</span>
                      {r.deals_closed > 0 && <span className="ml-auto text-emerald-700 font-bold">✓ {r.deals_closed} deal{r.deals_closed > 1 ? "s" : ""}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className={`font-display text-lg font-bold tabular-nums ${r.conversion_pct >= 30 ? "text-emerald-600" : r.conversion_pct >= 15 ? "text-amber-600" : "text-slate-700"}`} data-testid={`interest-conversion-${r.locality}`}>
                      {r.conversion_pct}%
                    </div>
                    <div className="text-[10px] text-slate-400 uppercase tracking-wide font-semibold">Conv.</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}


function SettingsPanel() {
  const [sub, setSub] = useState(() => localStorage.getItem("settings_sub") || "fees");
  const goSub = (s) => { setSub(s); localStorage.setItem("settings_sub", s); };
  return (
    <div className="p-4 md:p-5 space-y-4" data-testid="admin-settings-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><Settings2 size={18}/> Settings</h3>
          <p className="text-xs text-slate-500 mt-1">Platform fees, locality catalogue and portal controls in one place.</p>
        </div>
        <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5" data-testid="settings-subtab-toggle">
          <button onClick={() => goSub("fees")} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors flex items-center gap-1 ${sub === "fees" ? "bg-white text-kp-teal shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid="settings-tab-fees">
            <IndianRupee size={12}/> Manage Fees
          </button>
          <button onClick={() => goSub("localities")} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors flex items-center gap-1 ${sub === "localities" ? "bg-white text-kp-teal shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid="settings-tab-localities">
            <Globe size={12}/> Manage Localities
          </button>
        </div>
      </div>
      {sub === "fees" ? <FeeSettingsPanel/> : <LocalitiesPanel/>}
    </div>
  );
}

function LocalitiesPanel() {
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null); // null | {} (new) | locality
  const [loaded, setLoaded] = useState(false);
  const [importing, setImporting] = useState(false);
  const load = async () => {
    const r = await api.get("/localities");
    setList(r.data);
    setLoaded(true);
    return r.data;
  };
  const importDefaults = async (silent = false) => {
    setImporting(true);
    try {
      const r = await api.post("/admin/localities/import-defaults");
      if (!silent) {
        if (r.data.inserted > 0) toast.success(`Imported ${r.data.inserted} area(s) from the website · ${r.data.skipped} already present`);
        else toast.info(`All ${r.data.total} website areas are already in the catalogue`);
      } else if (r.data.inserted > 0) {
        toast.success(`Synced ${r.data.inserted} website areas into your catalogue`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); } finally { setImporting(false); }
  };
  useEffect(() => {
    (async () => {
      const items = await load();
      // Auto-import once if empty so admin never sees an empty catalogue while the site still shows areas
      if (items.length === 0) importDefaults(true);
    })();
    // eslint-disable-next-line
  }, []);
  const del = async (l) => {
    if (!window.confirm(`Delete locality "${l.name}"?\nProperties already tagged with this locality name will not be removed.`)) return;
    try {
      const r = await api.delete(`/admin/localities/${l.id}`);
      toast.success(r.data.linked_properties ? `Deleted. ${r.data.linked_properties} listing(s) still tagged with this name.` : "Locality deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const filtered = list.filter((l) => !q || l.name.toLowerCase().includes(q.toLowerCase()) || (l.region || "").toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm text-slate-600"><b className="text-slate-900">{list.length}</b> localities · <span className="text-slate-400">used across property listings, blog tags and promotions</span></div>
        <div className="flex items-center gap-2">
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or region..." className="w-56 h-9" data-testid="locality-search"/>
          <Button onClick={() => importDefaults(false)} disabled={importing} variant="outline" className="rounded-full gap-1 h-9 text-xs" data-testid="locality-sync-btn">
            <RefreshCw size={13} className={importing ? "animate-spin" : ""}/> Sync Website Areas
          </Button>
          <Button onClick={() => setEditing({})} className="btn-primary rounded-full gap-1 h-9" data-testid="locality-new-btn"><Plus size={14}/> Add Locality</Button>
        </div>
      </div>
      {loaded && list.length === 0 && (
        <div className="rounded-xl border-2 border-dashed border-kp-teal/40 bg-kp-teal-light/40 p-5 text-center" data-testid="locality-empty-cta">
          <Globe className="mx-auto text-kp-teal mb-2" size={24}/>
          <div className="font-semibold text-slate-800">No areas in your catalogue yet</div>
          <p className="text-xs text-slate-600 mt-1 mb-3">Import the 22 Surat areas already available in the site's locality picker.</p>
          <Button onClick={() => importDefaults(false)} disabled={importing} className="btn-primary rounded-full gap-2" data-testid="locality-import-cta">
            <Download size={14}/> {importing ? "Importing..." : "Import 22 Surat Areas"}
          </Button>
        </div>
      )}
      <div className="overflow-x-auto border rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-600 uppercase">
            <tr><th className="p-2">Name</th><th className="p-2">Region</th><th className="p-2 text-right">Avg ₹/sqft</th><th className="p-2 text-right">Listings</th><th className="p-2"></th></tr>
          </thead>
          <tbody>
            {filtered.map((l) => (
              <tr key={l.id} className="border-t hover:bg-slate-50" data-testid={`locality-row-${l.id}`}>
                <td className="p-2 font-semibold text-slate-800">{l.name}</td>
                <td className="p-2 text-slate-600">{l.region || <span className="text-slate-400">—</span>}</td>
                <td className="p-2 text-right tabular-nums">{l.avg_price_sqft ? `₹${Number(l.avg_price_sqft).toLocaleString("en-IN")}` : <span className="text-slate-400">—</span>}</td>
                <td className="p-2 text-right tabular-nums">{l.listings || 0}</td>
                <td className="p-2">
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => setEditing(l)} className="text-kp-teal hover:bg-kp-teal-light p-1.5 rounded" data-testid={`locality-edit-${l.id}`}><Edit size={14}/></button>
                    <button onClick={() => del(l)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" data-testid={`locality-delete-${l.id}`}><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
            {loaded && list.length > 0 && filtered.length === 0 && (
              <tr><td colSpan="5" className="p-8 text-center text-slate-500 italic">No localities match your search.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {editing && <LocalityDialog locality={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function LocalityDialog({ locality, onClose, onSaved }) {
  const isNew = !locality?.id;
  const [f, setF] = useState({
    name: locality?.name || "",
    region: locality?.region || "",
    avg_price_sqft: locality?.avg_price_sqft || "",
    listings: locality?.listings || 0,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: f.name.trim(),
        region: f.region.trim(),
        avg_price_sqft: parseFloat(f.avg_price_sqft) || 0,
        listings: parseInt(f.listings) || 0,
      };
      if (isNew) await api.post("/admin/localities", payload);
      else await api.patch(`/admin/localities/${locality.id}`, payload);
      toast.success(isNew ? "Locality added" : "Locality updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()} data-testid="locality-dialog">
        <h4 className="font-display text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
          <Globe size={18} className="text-kp-teal"/> {isNew ? "Add Locality" : "Edit Locality"}
        </h4>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-600">Name *</label>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Vesu" data-testid="locality-input-name"/>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Region</label>
            <Input value={f.region} onChange={(e) => set("region", e.target.value)} placeholder="e.g. South Surat" data-testid="locality-input-region"/>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-600">Avg ₹ / sqft</label>
              <Input type="number" value={f.avg_price_sqft} onChange={(e) => set("avg_price_sqft", e.target.value)} placeholder="e.g. 7500" data-testid="locality-input-price"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Listings</label>
              <Input type="number" value={f.listings} onChange={(e) => set("listings", e.target.value)} placeholder="e.g. 120" data-testid="locality-input-listings"/>
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <Button variant="outline" onClick={onClose} className="rounded-full">Cancel</Button>
          <Button onClick={save} disabled={busy} className="btn-primary rounded-full gap-2" data-testid="locality-save-btn">
            <Save size={14}/> {busy ? "Saving..." : "Save Locality"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FeeSettingsPanel() {
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings/fees").then((r) => setF(r.data)); }, []);
  if (!f) return <div className="p-8 text-center text-slate-500">Loading...</div>;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        sale_pct: parseFloat(f.sale_pct),
        rent_multiplier: parseFloat(f.rent_multiplier),
        lease_multiplier: parseFloat(f.lease_multiplier),
        gst_pct: parseFloat(f.gst_pct),
      };
      const r = await api.patch("/admin/settings/fees", payload);
      setF(r.data);
      toast.success("Fee structure updated");
    } catch { toast.error("Failed to save"); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-5" data-testid="admin-fee-settings">
      <div>
        <h3 className="font-display text-base font-semibold text-slate-900">Platform Fee Structure</h3>
        <p className="text-xs text-slate-500 mt-1">These values are shown to users on the Post Property fee preview.</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {[
          ["sale_pct", "Sale Fee (% of deal amount)", "%", "e.g. 1 = 1% commission on sale price"],
          ["rent_multiplier", "Rent Fee (× monthly rent)", "×", "e.g. 2 = two months' rent"],
          ["lease_multiplier", "Lease Fee (× monthly lease)", "×", "e.g. 2 = two months' lease"],
          ["gst_pct", "GST (%)", "%", "Applied on top of the platform fee"],
        ].map(([k, lbl, sfx, hint]) => (
          <div key={k}>
            <label className="text-xs font-semibold text-slate-600">{lbl}</label>
            <div className="relative">
              <Input type="number" step="0.1" value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`setting-${k}`}/>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">{sfx}</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{hint}</p>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="btn-primary rounded-full font-semibold flex items-center gap-2" data-testid="save-fee-settings">
        <Save size={16}/> {saving ? "Saving..." : "Save Fee Structure"}
      </Button>

      <DangerZone/>
    </div>
  );
}

function DangerZone() {
  const [busy, setBusy] = useState(false);
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const doReset = async () => {
    if (text !== "RESET") { toast.error('Type RESET exactly to confirm'); return; }
    setBusy(true);
    try {
      const r = await api.post("/admin/reset-data", { confirm: "RESET" });
      const total = Object.values(r.data.cleared || {}).reduce((s, n) => s + (n || 0), 0);
      toast.success(`Reset complete — cleared ${total} records. Ready for launch 🚀`);
      setOpen(false); setText("");
      setTimeout(() => nav("/admin"), 800);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-8 border-2 border-rose-200 bg-rose-50 rounded-xl p-4" data-testid="admin-danger-zone">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0"><AlertCircle size={18}/></div>
        <div className="flex-1">
          <div className="font-display font-bold text-rose-800">Danger Zone — Reset Portal</div>
          <p className="text-xs text-rose-700/80 mt-1 max-w-xl">
            Permanently deletes all properties, users (except admin), enquiries, CRM leads, ledger entries, invoices, promotions, blog posts, broadcasts, agents, projects and localities. Fee structure and your admin account are preserved. Demo data will <b>not</b> be re-seeded on restart.
          </p>
          {!open ? (
            <Button onClick={() => setOpen(true)} className="mt-3 bg-rose-600 hover:bg-rose-700 text-white rounded-full gap-2" data-testid="open-reset-btn">
              <Trash2 size={14}/> Reset All Data
            </Button>
          ) : (
            <div className="mt-3 space-y-2 max-w-md">
              <label className="text-xs font-semibold text-rose-700">Type <span className="font-mono bg-white px-1 rounded border">RESET</span> to confirm:</label>
              <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="RESET" className="bg-white" data-testid="reset-confirm-input"/>
              <div className="flex gap-2">
                <Button onClick={doReset} disabled={busy || text !== "RESET"} className="bg-rose-600 hover:bg-rose-700 text-white rounded-full gap-2" data-testid="confirm-reset-btn">
                  <Trash2 size={14}/> {busy ? "Resetting..." : "Confirm Reset"}
                </Button>
                <Button variant="outline" onClick={() => { setOpen(false); setText(""); }} className="rounded-full">Cancel</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PromotionsPanel() {
  const [list, setList] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/admin/promotions").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const toggle = async (p) => { await api.patch(`/admin/promotions/${p.id}`, { active: !p.active }); load(); };
  const del = async (p) => { if (!window.confirm(`Delete promotion for ${p.locality}?`)) return; await api.delete(`/admin/promotions/${p.id}`); load(); };
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="p-4 md:p-5 space-y-4" data-testid="admin-promotions">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900">Locality Promotions</h3>
          <p className="text-xs text-slate-500 mt-1">Set promotional fee waivers per locality (e.g., 100% off in Pandesara for 30 days) to attract inventory.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton kind="promotions"/>
          <Button onClick={() => setShowNew(true)} className="btn-primary rounded-full gap-1" data-testid="new-promo-btn"><Plus size={16}/> New Promotion</Button>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="text-center py-10 text-slate-500 border border-dashed rounded-xl">No promotions yet. Create one to drive listings in a locality.</div>
      ) : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-600 uppercase">
              <tr><th className="p-2">Locality</th><th className="p-2">Discount</th><th className="p-2">Scope</th><th className="p-2">Valid</th><th className="p-2">Note</th><th className="p-2">Status</th><th className="p-2"></th></tr>
            </thead>
            <tbody>
              {list.map((p) => {
                const live = p.active && p.valid_from <= today && p.valid_till >= today;
                return (
                  <tr key={p.id} className="border-t">
                    <td className="p-3 font-semibold">{p.locality}</td>
                    <td className="p-2"><span className="bg-emerald-100 text-emerald-700 text-xs font-bold px-2 py-0.5 rounded">{p.discount_pct}% OFF</span></td>
                    <td className="p-3 text-xs">{p.scope === "fee_gst" ? "Fee + GST" : "Fee only"}</td>
                    <td className="p-3 text-xs">{p.valid_from} → {p.valid_till}</td>
                    <td className="p-3 text-xs text-slate-500 max-w-[180px] truncate">{p.note || "—"}</td>
                    <td className="p-2">
                      {live ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-semibold">LIVE</span> :
                        p.active ? <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Scheduled</span> :
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Paused</span>}
                    </td>
                    <td className="p-2">
                      <div className="flex gap-1">
                        <button onClick={() => toggle(p)} className="text-kp-teal hover:bg-kp-teal-light p-1.5 rounded text-xs font-semibold" data-testid={`promo-toggle-${p.id}`}>{p.active ? "Pause" : "Activate"}</button>
                        <button onClick={() => del(p)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" data-testid={`promo-delete-${p.id}`}><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showNew && <NewPromotionDialog onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }}/>}
    </div>
  );
}

function NewPromotionDialog({ onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const in30 = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  const [f, setF] = useState({ locality: "ALL", discount_pct: 100, scope: "fee", valid_from: today, valid_till: in30, note: "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.locality || f.discount_pct <= 0 || f.discount_pct > 100) { toast.error("Discount must be between 1 and 100"); return; }
    setSaving(true);
    try {
      await api.post("/admin/promotions", { ...f, discount_pct: parseFloat(f.discount_pct) });
      toast.success("Promotion created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="new-promo-dialog">
        <DialogTitle className="font-display text-xl">New Locality Promotion</DialogTitle>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold text-slate-600">Locality</label>
            <Select value={f.locality} onValueChange={(v) => set("locality", v)}>
              <SelectTrigger data-testid="promo-locality"><SelectValue/></SelectTrigger>
              <SelectContent className="max-h-72"><SelectItem value="ALL">All Localities</SelectItem>{LOCALITIES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-600">Discount %</label>
              <Input type="number" min="1" max="100" value={f.discount_pct} onChange={(e) => set("discount_pct", e.target.value)} data-testid="promo-discount"/>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-600">Waiver Applies On</label>
              <Select value={f.scope} onValueChange={(v) => set("scope", v)}>
                <SelectTrigger data-testid="promo-scope"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="fee">Platform Fee only</SelectItem><SelectItem value="fee_gst">Fee + GST</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600">Valid From</label><Input type="date" value={f.valid_from} onChange={(e) => set("valid_from", e.target.value)}/></div>
            <div><label className="text-xs font-semibold text-slate-600">Valid Till</label><Input type="date" value={f.valid_till} onChange={(e) => set("valid_till", e.target.value)}/></div>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Note (shown to user)</label>
            <Input value={f.note} onChange={(e) => set("note", e.target.value)} placeholder="e.g. Diwali special — 100% fee waiver" data-testid="promo-note"/>
          </div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={save} disabled={saving} className="btn-primary flex-1 font-semibold" data-testid="promo-save">{saving ? "Saving..." : "Save Promotion"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CloseDealDialog({ property, onClose, onDone }) {
  const [f, setF] = useState({ deal_amount: property.price || "", buyer_name: "", buyer_phone: "", buyer_email: "", owner_email: property.owner_email || "" });
  const [q, setQ] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    const price = parseFloat(f.deal_amount) || 0;
    if (!price) { setQ(null); return; }
    const t = setTimeout(() => {
      api.get("/fees/quote", { params: { purpose: property.purpose, price, locality: property.locality } }).then((r) => setQ(r.data)).catch(() => setQ(null));
    }, 250);
    return () => clearTimeout(t);
  }, [f.deal_amount, property.purpose, property.locality]);

  const save = async () => {
    const amt = parseFloat(f.deal_amount);
    if (!amt || amt <= 0) { toast.error("Deal amount required"); return; }
    setSaving(true);
    try {
      const r = await api.post(`/admin/properties/${property.id}/close-deal`, f);
      toast.success(`Invoice ${r.data.invoice.invoice_no} generated & emailed`);
      onDone(r.data.invoice);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="close-deal-dialog">
        <DialogTitle className="font-display text-xl flex items-center gap-2"><Handshake size={20} className="text-kp-maroon"/> Close Deal & Generate Invoice</DialogTitle>
        <p className="text-xs text-slate-500 -mt-1">This will mark <b>{property.title}</b> as SOLD, generate a GST invoice, and email it to admin, seller & buyer.</p>
        <div className="space-y-3 mt-2">
          <div>
            <label className="text-xs font-semibold text-slate-600">Final Deal Amount (₹) *</label>
            <Input type="number" min="1" value={f.deal_amount} onChange={(e) => set("deal_amount", e.target.value)} placeholder="Actual closing amount" data-testid="close-deal-amount"/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600">Buyer / Tenant Name</label><Input value={f.buyer_name} onChange={(e) => set("buyer_name", e.target.value)} data-testid="close-buyer-name"/></div>
            <div><label className="text-xs font-semibold text-slate-600">Buyer Phone</label><Input value={f.buyer_phone} onChange={(e) => set("buyer_phone", e.target.value)} data-testid="close-buyer-phone"/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Mail size={11}/> Buyer Email (auto-invoice)</label><Input type="email" value={f.buyer_email} onChange={(e) => set("buyer_email", e.target.value)} placeholder="buyer@example.com" data-testid="close-buyer-email"/></div>
            <div><label className="text-xs font-semibold text-slate-600 flex items-center gap-1"><Mail size={11}/> Seller Email (auto-invoice)</label><Input type="email" value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} placeholder="seller@example.com" data-testid="close-owner-email"/></div>
          </div>
          {q && (
            <div className="bg-slate-50 border rounded-xl p-3 text-sm space-y-1">
              {q.promotion && (
                <div className="text-xs text-emerald-700 flex items-center gap-1 mb-1"><Tag size={12}/> {q.discount_pct}% promotional waiver on {q.promotion.locality}</div>
              )}
              <div className="flex justify-between"><span className="text-slate-600">Platform Fee ({q.label})</span><span>{inr(q.base_fee)}</span></div>
              {q.discount_pct > 0 && <div className="flex justify-between text-emerald-700"><span>Discount</span><span>- {inr(q.discount_amount)}</span></div>}
              {q.discount_pct > 0 && <div className="flex justify-between"><span className="text-slate-600">Net Fee</span><span>{inr(q.net_fee)}</span></div>}
              <div className="flex justify-between"><span className="text-slate-600">GST ({q.gst_pct}%)</span><span>{inr(q.gst_amount)}</span></div>
              <div className="flex justify-between text-kp-maroon font-bold pt-1 border-t"><span>Invoice Total</span><span>{inr(q.total)}</span></div>
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={save} disabled={saving} className="btn-primary flex-1 font-semibold flex items-center justify-center gap-1" data-testid="close-deal-save">
              <FileText size={16}/> {saving ? "Generating..." : "Close & Email Invoice"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BlogPanel() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/blog").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const del = async (p) => {
    if (!window.confirm(`Delete post "${p.title}"?`)) return;
    await api.delete(`/admin/blog/${p.id}`);
    toast.success("Post deleted");
    load();
  };
  const togglePub = async (p) => {
    await api.patch(`/admin/blog/${p.id}`, { published: !p.published });
    toast.success(p.published ? "Unpublished" : "Published");
    load();
  };
  return (
    <div className="p-4 md:p-5 space-y-4" data-testid="admin-blog-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><BookOpen size={18}/> Blog Articles</h3>
          <p className="text-xs text-slate-500 mt-1">Publish market insights and locality guides to drive organic search traffic.</p>
        </div>
        <div className="flex items-center gap-2">
          <ExportButton kind="blog"/>
          <Button onClick={() => setEditing({})} className="btn-primary rounded-full gap-1" data-testid="new-blog-btn"><Plus size={16}/> New Article</Button>
        </div>
      </div>
      {list.length === 0 ? (
        <div className="text-center py-10 text-slate-500 border border-dashed rounded-xl">No articles yet. Write your first one!</div>
      ) : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-600 uppercase">
              <tr><th className="p-2">Title</th><th className="p-2">Locality</th><th className="p-2">Tags</th><th className="p-2">Status</th><th className="p-2">Views</th><th className="p-2">Updated</th><th className="p-2"></th></tr>
            </thead>
            <tbody>
              {list.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="p-3 font-semibold max-w-sm">
                    <div className="flex items-center gap-2">
                      {p.cover_image && <img src={p.cover_image} alt="" className="w-10 h-10 rounded object-cover"/>}
                      <div className="min-w-0">
                        <div className="truncate">{p.title}</div>
                        <div className="text-xs text-slate-500 font-mono truncate">/{p.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-xs">{p.locality || "—"}</td>
                  <td className="p-3 text-xs text-slate-500">{(p.tags || []).slice(0, 3).join(", ") || "—"}</td>
                  <td className="p-2">
                    {p.published ? <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-semibold">Published</span> :
                      <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">Draft</span>}
                  </td>
                  <td className="p-3 text-xs">{p.views}</td>
                  <td className="p-3 text-xs text-slate-500">{new Date(p.updated_at).toLocaleDateString()}</td>
                  <td className="p-2">
                    <div className="flex gap-1">
                      <button onClick={() => togglePub(p)} className="text-kp-teal hover:bg-kp-teal-light p-1.5 rounded text-xs font-semibold" data-testid={`blog-toggle-${p.id}`}>{p.published ? "Unpublish" : "Publish"}</button>
                      <button onClick={() => api.get(`/blog/${p.slug}`).then((r) => setEditing({ ...r.data }))} className="text-kp-teal hover:bg-kp-teal-light p-1.5 rounded" data-testid={`blog-edit-${p.id}`}><Edit size={14}/></button>
                      <button onClick={() => del(p)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" data-testid={`blog-delete-${p.id}`}><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing && <BlogEditorDialog post={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function BlogEditorDialog({ post, onClose, onSaved }) {
  const isEdit = !!post.id;
  const [f, setF] = useState({
    title: post.title || "", excerpt: post.excerpt || "", content: post.content || "",
    cover_image: post.cover_image || "", locality: post.locality || "",
    tags: (post.tags || []).join(", "), published: post.published !== false,
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const payload = { ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean) };
      if (isEdit) await api.patch(`/admin/blog/${post.id}`, payload);
      else await api.post("/admin/blog", payload);
      toast.success(isEdit ? "Article updated" : "Article published");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="blog-editor-dialog">
        <DialogTitle className="font-display text-xl">{isEdit ? "Edit Article" : "New Article"}</DialogTitle>
        <div className="space-y-3 mt-2">
          <div><label className="text-xs font-semibold text-slate-600">Title *</label><Input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Vesu Real Estate Trends 2026" data-testid="blog-title-input"/></div>
          <div><label className="text-xs font-semibold text-slate-600">Excerpt (short summary)</label><Textarea rows={2} value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} placeholder="One-line teaser shown in listings"/></div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold text-slate-600">Locality (optional)</label>
              <Select value={f.locality || "none"} onValueChange={(v) => set("locality", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="None"/></SelectTrigger>
                <SelectContent className="max-h-72"><SelectItem value="none">General (all Surat)</SelectItem>{LOCALITIES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-xs font-semibold text-slate-600">Tags (comma separated)</label><Input value={f.tags} onChange={(e) => set("tags", e.target.value)} placeholder="market, guide, rera"/></div>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">Cover Image URL (optional)</label><Input value={f.cover_image} onChange={(e) => set("cover_image", e.target.value)} placeholder="https://…"/></div>
          <div>
            <label className="text-xs font-semibold text-slate-600">Content (Markdown supported: # H1, ## H2, - list, **bold**, [link](url))</label>
            <Textarea rows={14} value={f.content} onChange={(e) => set("content", e.target.value)} className="font-mono text-sm" placeholder={"# Heading\n\nYour article body…\n\n## Section\n- point one\n- point two"} data-testid="blog-content-input"/>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.published} onChange={(e) => set("published", e.target.checked)} data-testid="blog-published"/> Publish immediately</label>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={save} disabled={saving} className="btn-primary flex-1 font-semibold" data-testid="blog-save">{saving ? "Saving…" : (isEdit ? "Update Article" : "Publish Article")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function HotnessScore({ score }) {
  const s = Math.max(0, Math.min(100, score || 0));
  const tier = s >= 75 ? { label: "🔥", color: "bg-red-500 text-white", pulse: true }
    : s >= 50 ? { label: "🌡️", color: "bg-orange-500 text-white" }
    : s >= 25 ? { label: "•", color: "bg-amber-400 text-white" }
    : { label: "•", color: "bg-slate-300 text-slate-700" };
  return (
    <span title={`Hotness score ${s}/100 · views + interests + conversion + freshness`} data-testid={`admin-hotness-${s}`}
      className={`inline-flex items-center gap-1 text-[10px] font-black tabular-nums px-1.5 py-0.5 rounded-full ${tier.color} ${tier.pulse ? "animate-pulse" : ""}`}>
      <span>{tier.label}</span><span>{s}</span>
    </span>
  );
}

const INTEREST_STATUSES = [
  { k: "new", label: "New", chip: "bg-blue-100 text-blue-700 border-blue-200" },
  { k: "contacted", label: "Contacted", chip: "bg-purple-100 text-purple-700 border-purple-200" },
  { k: "shortlisted", label: "Shortlisted", chip: "bg-amber-100 text-amber-700 border-amber-200" },
  { k: "rejected", label: "Rejected", chip: "bg-slate-200 text-slate-600 border-slate-300" },
  { k: "converted", label: "Converted", chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
];
const interestMeta = (k) => INTEREST_STATUSES.find((s) => s.k === (k || "new")) || INTEREST_STATUSES[0];

function PropertiesTable({ list, tab, onVerify, onFeature, onDelete, onEdit, onClose }) {
  const [expanded, setExpanded] = useState(null);
  return (
    <>
      <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50">
        <div className="text-sm text-slate-600">
          {tab === "pending" ? "Properties awaiting verification" : "All properties — owner details & interested-buyer manager"}
        </div>
        <ExportButton kind="properties"/>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-600 uppercase">
            <tr>
              <th className="p-2 w-10 text-center">#</th>
              <th className="p-2">Posted By</th>
              <th className="p-2">Property</th>
              <th className="p-2 whitespace-nowrap">Price · Purpose</th>
              <th className="p-2 text-center">Interests</th>
              <th className="p-2">Status</th>
              <th className="p-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p, idx) => {
              const isExp = expanded === p.id;
              return (
                <React.Fragment key={p.id}>
                  <tr className={`border-t ${isExp ? "bg-kp-teal-light/30" : "hover:bg-slate-50"}`} data-testid={`admin-property-row-${p.id}`}>
                    <td className="p-2 text-center text-xs font-bold text-slate-500 tabular-nums">{idx + 1}</td>
                    <td className="p-2 align-top">
                      <div className="font-semibold text-slate-800 text-[13px]">{p.owner_name || <span className="text-slate-400">Unknown</span>}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex flex-col gap-0.5">
                        {p.owner_phone && <a href={`tel:${p.owner_phone}`} className="hover:text-kp-teal">📞 {p.owner_phone}</a>}
                        {p.owner_email && <a href={`mailto:${p.owner_email}`} className="hover:text-kp-teal truncate max-w-[160px]">✉ {p.owner_email}</a>}
                      </div>
                    </td>
                    <td className="p-2 align-top max-w-[280px]">
                      <div className="flex items-start gap-2">
                        {p.images?.[0] && <img src={p.images[0]} alt="" className="w-11 h-11 rounded object-cover shrink-0"/>}
                        <div className="min-w-0">
                          <a href={`/property/${p.id}`} target="_blank" rel="noreferrer" className="font-semibold text-slate-900 text-[13px] hover:text-kp-teal line-clamp-1">{p.title}</a>
                          <div className="text-[11px] text-slate-500 mt-0.5 capitalize">{p.property_type || p.category} · {p.locality}{p.bedrooms ? ` · ${p.bedrooms}BHK` : ""}{p.area ? ` · ${p.area} sqft` : ""}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-2 align-top whitespace-nowrap">
                      <div className="font-bold text-kp-maroon text-[13px]">{p.price_label || inr(p.price)}</div>
                      <div className="text-[11px] text-slate-500 capitalize">For {p.purpose}</div>
                    </td>
                    <td className="p-2 align-top text-center">
                      <button onClick={() => setExpanded(isExp ? null : p.id)} data-testid={`admin-interests-toggle-${p.id}`}
                        className={`relative inline-flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg border transition-colors ${p.interest_count > 0 ? "border-kp-teal bg-kp-teal-light hover:bg-kp-teal hover:text-white" : "border-slate-200 bg-white text-slate-500"}`}>
                        <span className="font-bold tabular-nums text-sm">{p.interest_count || 0}</span>
                        <span className="text-[9px] uppercase tracking-wide">{isExp ? "hide" : "manage"}</span>
                        {p.interest_stale > 0 && (
                          <span className="absolute -top-1.5 -right-1.5 flex h-4 w-4" title={`${p.interest_stale} follow-up${p.interest_stale > 1 ? "s" : ""} overdue (3+ days)`} data-testid={`admin-stale-dot-${p.id}`}>
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"/>
                            <span className="relative inline-flex rounded-full h-4 w-4 bg-red-500 text-white text-[9px] font-black items-center justify-center border-2 border-white">{p.interest_stale}</span>
                          </span>
                        )}
                      </button>
                      {p.interest_new > 0 && !isExp && (
                        <div className="text-[9px] text-blue-600 font-bold mt-1">🔵 {p.interest_new} new</div>
                      )}
                    </td>
                    <td className="p-2 align-top">
                      <div className="flex items-center gap-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${p.status === "active" ? "bg-emerald-100 text-emerald-700" : p.status === "sold" ? "bg-slate-800 text-white" : "bg-amber-100 text-amber-700"}`}>
                          {p.status === "pending" && <Clock size={10}/>}
                          {p.status === "sold" && <CheckCircle size={10}/>}
                          {p.status}
                        </span>
                        <HotnessScore score={p.hotness_score || 0}/>
                      </div>
                      <div className="text-[10px] text-slate-500 mt-1 space-x-1">
                        {p.verified && <span className="text-emerald-600 font-semibold">✓ verified</span>}
                        {p.featured && <span className="text-amber-600 font-semibold">★ featured</span>}
                      </div>
                    </td>
                    <td className="p-2 align-top">
                      <div className="flex gap-1 justify-end">
                        {!p.verified && <button onClick={() => onVerify(p.id)} className="text-emerald-600 hover:bg-emerald-50 p-1.5 rounded" title="Verify" data-testid={`admin-verify-${p.id}`}><CheckCircle size={16}/></button>}
                        <button onClick={() => onEdit(p)} className="text-kp-teal hover:bg-kp-teal-light p-1.5 rounded" title="Edit" data-testid={`admin-edit-${p.id}`}><Edit size={16}/></button>
                        <button onClick={() => onFeature(p.id)} className="text-amber-600 hover:bg-amber-50 p-1.5 rounded" title="Feature" data-testid={`admin-feature-${p.id}`}><Star size={16}/></button>
                        {p.status !== "sold" && (
                          <button onClick={() => onClose(p)} className="text-kp-maroon hover:bg-kp-maroon-light p-1.5 rounded" title="Close Deal & Invoice" data-testid={`admin-close-${p.id}`}><Handshake size={16}/></button>
                        )}
                        <button onClick={() => onDelete(p.id)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" title="Delete" data-testid={`admin-delete-${p.id}`}><Trash2 size={16}/></button>
                      </div>
                    </td>
                  </tr>
                  {isExp && (
                    <tr className="bg-kp-teal-light/20 border-t border-kp-teal/20">
                      <td colSpan={7} className="p-0"><InterestsPanel property={p}/></td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
            {list.length === 0 && (
              <tr><td colSpan={7} className="p-8 text-center text-slate-500">
                {tab === "pending" ? "No pending property requests. All caught up! ✓" : "No properties yet — post your first listing to get started."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function InterestsPanel({ property }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = () => api.get(`/admin/properties/${property.id}/interests`).then((r) => setData(r.data));
  useEffect(() => { load(); }, [property.id]);
  const setStatus = async (eid, status) => {
    setBusy(eid + status);
    try {
      await api.patch(`/admin/enquiries/${eid}/status`, { status });
      toast.success(`Marked ${interestMeta(status).label}`);
      load();
    } catch { toast.error("Update failed"); } finally { setBusy(null); }
  };
  const promote = async (eid, name) => {
    setBusy(eid + "promote");
    try {
      const r = await api.post(`/admin/enquiries/${eid}/promote`);
      if (r.data.already_promoted) toast.info(`${name} already promoted`);
      else toast.success(`${name} → CRM lead`);
      await api.patch(`/admin/enquiries/${eid}/status`, { status: "converted" });
      load();
    } catch { toast.error("Promote failed"); } finally { setBusy(null); }
  };

  if (!data) return <div className="px-6 py-4 text-xs text-slate-500">Loading interests…</div>;
  const items = data.interests || [];
  const isRent = property.purpose === "rent" || property.purpose === "lease";
  const label = isRent ? "Tenant" : "Buyer";

  return (
    <div className="px-4 py-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold text-kp-teal uppercase tracking-widest flex items-center gap-2">
          <MessageSquare size={13}/> {items.length} {label}{items.length !== 1 ? "s" : ""} Interested
          {isRent && <span className="text-[10px] font-normal text-slate-500 normal-case">· rental enquiries</span>}
        </div>
        <a href={`/property/${property.id}`} target="_blank" rel="noreferrer" className="text-[11px] text-kp-teal hover:underline">View listing ↗</a>
      </div>
      {items.length === 0 ? (
        <div className="text-center py-6 text-sm text-slate-400 italic border border-dashed border-slate-200 rounded-lg bg-white">
          No {label.toLowerCase()} has submitted interest yet.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((e) => {
            const m = interestMeta(e.interest_status);
            const lastTouch = e.status_updated_at || e.created_at;
            const daysStale = lastTouch ? Math.floor((Date.now() - new Date(lastTouch).getTime()) / 86400000) : 0;
            const isStale = daysStale >= 3 && ["new", "contacted"].includes(e.interest_status || "new");
            return (
              <div key={e.id} className={`bg-white border rounded-xl p-3 flex flex-wrap items-start gap-3 transition-colors ${isStale ? "border-red-300 shadow-[0_0_0_3px_rgba(239,68,68,0.08)]" : "border-slate-200"}`} data-testid={`interest-row-${e.id}`}>
                <div className="relative w-9 h-9 rounded-full bg-kp-teal-light text-kp-teal flex items-center justify-center font-bold text-sm shrink-0">
                  {e.name?.[0]?.toUpperCase() || "?"}
                  {isStale && (
                    <span className="absolute -top-0.5 -right-0.5 flex h-3 w-3" title={`No update in ${daysStale} day${daysStale > 1 ? "s" : ""} — follow up now`} data-testid={`interest-stale-${e.id}`}>
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"/>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border-2 border-white"/>
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-slate-900 text-[13px]">{e.name}</div>
                    <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${m.chip}`} data-testid={`interest-status-${e.id}`}>{m.label}</span>
                    {isStale && (
                      <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full uppercase tracking-wide">
                        ⏰ Overdue · {daysStale}d
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 flex items-center flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                    <a href={`tel:${e.phone}`} className="hover:text-kp-teal">📞 {e.phone}</a>
                    {e.email && <a href={`mailto:${e.email}`} className="hover:text-kp-teal">✉ {e.email}</a>}
                    <span>· {new Date(e.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                  {e.message && <div className="text-[12px] text-slate-700 mt-1 italic">"{e.message}"</div>}
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <a href={`https://wa.me/91${e.phone}?text=${encodeURIComponent(`Hi ${e.name}, thanks for your interest in ${property.title}.`)}`}
                    target="_blank" rel="noreferrer"
                    className="text-[11px] font-semibold px-2 py-1.5 rounded-lg bg-[#25D366] text-white hover:bg-[#128C7E] flex items-center gap-1" data-testid={`interest-wa-${e.id}`}>
                    <MessageCircle size={12}/> WhatsApp
                  </a>
                  {["contacted", "shortlisted", "rejected"].map((s) => (
                    <button key={s} onClick={() => setStatus(e.id, s)} disabled={busy === e.id + s || e.interest_status === s}
                      className={`text-[11px] font-semibold px-2 py-1.5 rounded-lg border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${interestMeta(s).chip} hover:brightness-95`} data-testid={`interest-set-${s}-${e.id}`}>
                      {interestMeta(s).label}
                    </button>
                  ))}
                  {!e.promoted_lead_id ? (
                    <button onClick={() => promote(e.id, e.name)} disabled={busy === e.id + "promote"}
                      className="text-[11px] font-semibold px-2 py-1.5 rounded-lg bg-kp-teal text-white hover:bg-kp-teal-dark flex items-center gap-1 disabled:opacity-50" data-testid={`interest-promote-${e.id}`}>
                      <UserPlus size={12}/> To CRM
                    </button>
                  ) : (
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 px-2 py-1.5 rounded-lg flex items-center gap-1"><CheckCircle2 size={11}/> In CRM</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}



const LEAD_STATUSES = [
  { k: "new", label: "New", chip: "bg-blue-100 text-blue-700", col: "bg-blue-50 border-blue-200", accent: "border-t-blue-500" },
  { k: "contacted", label: "Contacted", chip: "bg-purple-100 text-purple-700", col: "bg-purple-50 border-purple-200", accent: "border-t-purple-500" },
  { k: "site_visit", label: "Site Visit", chip: "bg-amber-100 text-amber-700", col: "bg-amber-50 border-amber-200", accent: "border-t-amber-500" },
  { k: "negotiating", label: "Negotiating", chip: "bg-orange-100 text-orange-700", col: "bg-orange-50 border-orange-200", accent: "border-t-orange-500" },
  { k: "won", label: "Won", chip: "bg-emerald-100 text-emerald-700", col: "bg-emerald-50 border-emerald-200", accent: "border-t-emerald-500" },
  { k: "lost", label: "Lost", chip: "bg-slate-200 text-slate-600", col: "bg-slate-50 border-slate-200", accent: "border-t-slate-400" },
];

function CRMPanel() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [view, setView] = useState(() => localStorage.getItem("crm_view") || "kanban");
  const setViewMode = (v) => { setView(v); localStorage.setItem("crm_view", v); };
  const load = () => api.get("/admin/leads").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const del = async (l) => {
    if (!window.confirm(`Delete lead "${l.name}"?`)) return;
    await api.delete(`/admin/leads/${l.id}`);
    toast.success("Lead deleted"); load();
  };
  const statusMeta = (k) => LEAD_STATUSES.find((s) => s.k === k) || { label: k, chip: "bg-slate-100 text-slate-600" };
  const counts = LEAD_STATUSES.reduce((acc, s) => ({ ...acc, [s.k]: list.filter((l) => l.status === s.k).length }), {});
  const filtered = filterStatus === "all" ? list : list.filter((l) => l.status === filterStatus);
  const moveLead = async (leadId, newStatus) => {
    const lead = list.find((l) => l.id === leadId);
    if (!lead || lead.status === newStatus) return;
    // Optimistic update
    setList((s) => s.map((l) => (l.id === leadId ? { ...l, status: newStatus } : l)));
    try {
      await api.patch(`/admin/leads/${leadId}`, { status: newStatus });
      toast.success(`Moved "${lead.name}" → ${statusMeta(newStatus).label}`);
    } catch {
      toast.error("Move failed"); load();
    }
  };
  const download = async (fmt) => {
    try {
      const res = await api.get(`/admin/export/leads`, { params: { fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `krishna-property-leads-${new Date().toISOString().slice(0,10)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-4 md:p-5 space-y-4" data-testid="admin-crm-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><UserPlus size={18}/> CRM — Leads Pipeline</h3>
          <p className="text-xs text-slate-500 mt-1">Track every lead from first contact to deal-closed. Drag cards between columns to update status.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-full border border-slate-200 bg-slate-50 p-0.5" data-testid="crm-view-toggle">
            <button onClick={() => setViewMode("kanban")} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${view === "kanban" ? "bg-white text-kp-teal shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid="crm-view-kanban">Kanban</button>
            <button onClick={() => setViewMode("table")} className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${view === "table" ? "bg-white text-kp-teal shadow-sm" : "text-slate-500 hover:text-slate-800"}`} data-testid="crm-view-table">Table</button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="sm" variant="outline" className="rounded-full gap-1 text-xs" data-testid="export-leads-btn"><Download size={13}/> Export</Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => download("xlsx")}><FileSpreadsheet size={14} className="mr-2"/> Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => download("pdf")}><FileText size={14} className="mr-2"/> PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setEditing({})} className="btn-primary rounded-full gap-1" data-testid="new-lead-btn"><Plus size={16}/> New Lead</Button>
        </div>
      </div>

      {view === "table" && (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <button onClick={() => setFilterStatus("all")} className={`text-left rounded-xl p-3 border ${filterStatus === "all" ? "border-kp-teal bg-kp-teal-light" : "border-slate-200 hover:border-kp-teal"}`} data-testid="lead-filter-all">
            <div className="text-lg font-bold text-slate-900">{list.length}</div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">All Leads</div>
          </button>
          {LEAD_STATUSES.map((s) => (
            <button key={s.k} onClick={() => setFilterStatus(s.k)} className={`text-left rounded-xl p-3 border ${filterStatus === s.k ? "border-kp-teal bg-kp-teal-light" : "border-slate-200 hover:border-kp-teal"}`} data-testid={`lead-filter-${s.k}`}>
              <div className="text-lg font-bold text-slate-900">{counts[s.k] || 0}</div>
              <div className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">{s.label}</div>
            </button>
          ))}
        </div>
      )}

      {view === "kanban" ? (
        <KanbanBoard leads={list} onMove={moveLead} onEdit={setEditing} onDelete={del}/>
      ) : filtered.length === 0 ? (
        <div className="text-center py-10 text-slate-500 border border-dashed rounded-xl">No leads {filterStatus !== "all" ? `in "${statusMeta(filterStatus).label}"` : "yet"}. Add one to get started.</div>
      ) : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-600 uppercase">
              <tr><th className="p-2">Lead</th><th className="p-2">Phone/Email</th><th className="p-2">Interest</th><th className="p-2">Source</th><th className="p-2">Follow-up</th><th className="p-2">Status</th><th className="p-2"></th></tr>
            </thead>
            <tbody>
              {filtered.map((l) => {
                const m = statusMeta(l.status);
                const followupSoon = l.next_followup && new Date(l.next_followup) <= new Date(Date.now() + 2 * 86400000);
                return (
                  <tr key={l.id} className="border-t hover:bg-slate-50">
                    <td className="p-2">
                      <div className="font-semibold text-slate-800">{l.name}</div>
                      {l.budget && <div className="text-xs text-slate-500">Budget: {l.budget}</div>}
                    </td>
                    <td className="p-3 text-xs">
                      <div>{l.phone}</div>
                      {l.email && <div className="text-slate-500">{l.email}</div>}
                    </td>
                    <td className="p-3 text-xs">
                      {l.interested_property_title || <span className="text-slate-400">—</span>}
                      {l.locality && <div className="text-slate-500">{l.locality}</div>}
                    </td>
                    <td className="p-3 text-xs capitalize">{l.source.replace("_", " ")}</td>
                    <td className="p-3 text-xs">
                      {l.next_followup ? (
                        <span className={`flex items-center gap-1 ${followupSoon ? "text-kp-maroon font-semibold" : "text-slate-600"}`}>
                          <Calendar size={11}/> {new Date(l.next_followup).toLocaleDateString("en-IN", {day:"numeric",month:"short"})}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-2"><span className={`text-xs font-semibold px-2 py-0.5 rounded ${m.chip}`}>{m.label}</span></td>
                    <td className="p-2">
                      <div className="flex gap-1">
                        <button onClick={() => setEditing(l)} className="text-kp-teal hover:bg-kp-teal-light p-1.5 rounded" data-testid={`lead-edit-${l.id}`}><Edit size={14}/></button>
                        <button onClick={() => del(l)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" data-testid={`lead-delete-${l.id}`}><Trash2 size={14}/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {editing && <LeadDialog lead={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function KanbanBoard({ leads, onMove, onEdit, onDelete }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const byStatus = LEAD_STATUSES.reduce((acc, s) => ({ ...acc, [s.k]: leads.filter((l) => l.status === s.k) }), {});
  const onDragStart = (e, id) => {
    setDragId(id);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", id); } catch {}
  };
  const onDragEnd = () => { setDragId(null); setOverCol(null); };
  const onDragOver = (e, colKey) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; if (overCol !== colKey) setOverCol(colKey); };
  const onDrop = (e, colKey) => {
    e.preventDefault();
    const id = dragId || e.dataTransfer.getData("text/plain");
    setOverCol(null); setDragId(null);
    if (id) onMove(id, colKey);
  };

  return (
    <div className="overflow-x-auto pb-2" data-testid="crm-kanban">
      <div className="grid grid-flow-col auto-cols-[minmax(260px,1fr)] gap-3 min-w-full">
        {LEAD_STATUSES.map((s) => {
          const colLeads = byStatus[s.k] || [];
          const isOver = overCol === s.k;
          return (
            <div key={s.k}
              onDragOver={(e) => onDragOver(e, s.k)}
              onDrop={(e) => onDrop(e, s.k)}
              onDragLeave={() => overCol === s.k && setOverCol(null)}
              className={`rounded-2xl border-2 ${s.col} border-t-4 ${s.accent} p-2 flex flex-col transition-all ${isOver ? "ring-2 ring-kp-teal ring-offset-2 ring-offset-white scale-[1.01]" : ""}`}
              data-testid={`kanban-col-${s.k}`}>
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className={`text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded ${s.chip}`}>{s.label}</span>
                <span className="text-xs font-bold text-slate-500 bg-white/70 rounded-full px-2 py-0.5" data-testid={`kanban-count-${s.k}`}>{colLeads.length}</span>
              </div>
              <div className="flex-1 mt-1 space-y-2 min-h-[80px]">
                {colLeads.length === 0 ? (
                  <div className="text-center text-[11px] text-slate-400 italic py-6 border border-dashed border-slate-300 rounded-lg bg-white/40">Drop leads here</div>
                ) : colLeads.map((l) => {
                  const followupSoon = l.next_followup && new Date(l.next_followup) <= new Date(Date.now() + 2 * 86400000);
                  return (
                    <div key={l.id}
                      draggable
                      onDragStart={(e) => onDragStart(e, l.id)}
                      onDragEnd={onDragEnd}
                      className={`bg-white rounded-xl p-3 border border-slate-200 shadow-sm hover:shadow-md hover:border-kp-teal cursor-grab active:cursor-grabbing transition-all ${dragId === l.id ? "opacity-40 rotate-1" : ""}`}
                      data-testid={`kanban-card-${l.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="font-semibold text-sm text-slate-800 truncate">{l.name}</div>
                        <div className="flex gap-0.5 shrink-0">
                          <button onClick={() => onEdit(l)} className="text-kp-teal hover:bg-kp-teal-light p-1 rounded" data-testid={`kanban-edit-${l.id}`} title="Edit"><Edit size={12}/></button>
                          <button onClick={() => onDelete(l)} className="text-red-600 hover:bg-red-50 p-1 rounded" data-testid={`kanban-delete-${l.id}`} title="Delete"><Trash2 size={12}/></button>
                        </div>
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5 truncate">{l.phone}{l.email ? ` · ${l.email}` : ""}</div>
                      {l.interested_property_title && (
                        <div className="text-xs text-kp-teal mt-1.5 flex items-start gap-1"><Building2 size={11} className="mt-0.5 shrink-0"/><span className="truncate">{l.interested_property_title}</span></div>
                      )}
                      {(l.budget || l.locality) && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {l.budget && <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{l.budget}</span>}
                          {l.locality && <span className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{l.locality}</span>}
                        </div>
                      )}
                      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-100">
                        <span className="text-[10px] text-slate-400 capitalize">{(l.source || "manual").replace("_", " ")}</span>
                        {l.next_followup && (
                          <span className={`inline-flex items-center gap-1 text-[10px] ${followupSoon ? "text-kp-maroon font-semibold" : "text-slate-500"}`}>
                            <Calendar size={10}/> {new Date(l.next_followup).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function LeadDialog({ lead, onClose, onSaved }) {
  const isEdit = !!lead.id;
  const [f, setF] = useState({
    name: lead.name || "", phone: lead.phone || "", email: lead.email || "",
    source: lead.source || "manual",
    interested_property_id: lead.interested_property_id || "",
    interested_property_title: lead.interested_property_title || "",
    budget: lead.budget || "", locality: lead.locality || "",
    status: lead.status || "new", next_followup: lead.next_followup || "",
    notes: lead.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    if (!f.name.trim() || !f.phone.trim()) { toast.error("Name and phone are required"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.patch(`/admin/leads/${lead.id}`, f);
      else await api.post("/admin/leads", f);
      toast.success(isEdit ? "Lead updated" : "Lead added");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="lead-dialog">
        <DialogTitle className="font-display text-xl">{isEdit ? "Edit Lead" : "New Lead"}</DialogTitle>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600">Name *</label><Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="lead-name"/></div>
            <div><label className="text-xs font-semibold text-slate-600">Phone *</label><Input value={f.phone} onChange={(e) => set("phone", e.target.value)} data-testid="lead-phone"/></div>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">Email</label><Input type="email" value={f.email} onChange={(e) => set("email", e.target.value)}/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600">Source</label>
              <Select value={f.source} onValueChange={(v) => set("source", v)}><SelectTrigger data-testid="lead-source"><SelectValue/></SelectTrigger><SelectContent>
                <SelectItem value="website">Website</SelectItem><SelectItem value="enquiry">Enquiry Form</SelectItem>
                <SelectItem value="walk_in">Walk-in</SelectItem><SelectItem value="referral">Referral</SelectItem>
                <SelectItem value="social">Social Media</SelectItem><SelectItem value="manual">Manual</SelectItem>
              </SelectContent></Select>
            </div>
            <div><label className="text-xs font-semibold text-slate-600">Status</label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger data-testid="lead-status"><SelectValue/></SelectTrigger><SelectContent>
                <SelectItem value="new">New</SelectItem><SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="site_visit">Site Visit</SelectItem><SelectItem value="negotiating">Negotiating</SelectItem>
                <SelectItem value="won">Won</SelectItem><SelectItem value="lost">Lost</SelectItem>
              </SelectContent></Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600">Budget</label><Input value={f.budget} onChange={(e) => set("budget", e.target.value)} placeholder="e.g. ₹1 Cr"/></div>
            <div><label className="text-xs font-semibold text-slate-600">Preferred Locality</label>
              <Select value={f.locality || "none"} onValueChange={(v) => set("locality", v === "none" ? "" : v)}>
                <SelectTrigger><SelectValue placeholder="Any"/></SelectTrigger>
                <SelectContent className="max-h-72"><SelectItem value="none">Any</SelectItem>{LOCALITIES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">Interested Property (title)</label><Input value={f.interested_property_title} onChange={(e) => set("interested_property_title", e.target.value)} placeholder="e.g. 3 BHK in Vesu"/></div>
          <div><label className="text-xs font-semibold text-slate-600">Next Follow-up Date</label><Input type="date" value={f.next_followup} onChange={(e) => set("next_followup", e.target.value)} data-testid="lead-followup"/></div>
          <div><label className="text-xs font-semibold text-slate-600">Notes</label><Textarea rows={3} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Call notes, preferences, objections..."/></div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={save} disabled={saving} className="btn-primary flex-1 font-semibold" data-testid="lead-save">{saving ? "Saving..." : (isEdit ? "Update Lead" : "Add Lead")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LedgerPanel() {
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filter, setFilter] = useState({ type: "all", status: "all", from: "", to: "" });
  const [editing, setEditing] = useState(null);
  const load = () => {
    const params = {};
    if (filter.type !== "all") params.entry_type = filter.type;
    if (filter.status !== "all") params.status = filter.status;
    if (filter.from) params.date_from = filter.from;
    if (filter.to) params.date_to = filter.to;
    api.get("/admin/ledger", { params }).then((r) => setList(r.data));
    const sp = {};
    if (filter.from) sp.date_from = filter.from;
    if (filter.to) sp.date_to = filter.to;
    api.get("/admin/ledger/summary", { params: sp }).then((r) => setSummary(r.data));
  };
  useEffect(() => { load(); }, [filter.type, filter.status, filter.from, filter.to]);
  const del = async (e) => {
    if (!window.confirm(`Delete entry "${e.particular}"?`)) return;
    await api.delete(`/admin/ledger/${e.id}`);
    toast.success("Entry deleted"); load();
  };
  const togglePaid = async (e) => {
    await api.patch(`/admin/ledger/${e.id}`, { status: e.status === "paid" ? "unpaid" : "paid" });
    load();
  };
  const download = async (fmt) => {
    try {
      const res = await api.get(`/admin/export/ledger`, { params: { fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `krishna-property-account-book-${new Date().toISOString().slice(0,10)}.${fmt}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };
  const modeLabel = { cash: "Cash", upi: "UPI", bank: "Bank Transfer", cheque: "Cheque", card: "Card" };

  return (
    <div className="p-4 md:p-5 space-y-4" data-testid="admin-ledger-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><BookMarked size={18}/> Account Book — Income & Expense</h3>
          <p className="text-xs text-slate-500 mt-1">Track your office income and expenses. Paid vs unpaid tracking with automatic P&L.</p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild><Button size="sm" variant="outline" className="rounded-full gap-1 text-xs" data-testid="export-ledger-btn"><Download size={13}/> Export</Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => download("xlsx")}><FileSpreadsheet size={14} className="mr-2"/> Excel</DropdownMenuItem>
              <DropdownMenuItem onClick={() => download("pdf")}><FileText size={14} className="mr-2"/> PDF</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={() => setEditing({})} className="btn-primary rounded-full gap-1" data-testid="new-ledger-btn"><Plus size={16}/> New Entry</Button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4">
            <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold flex items-center gap-1"><ArrowDownCircle size={12}/> Total Income</div>
            <div className="font-display text-xl font-bold text-emerald-800 mt-1">{inr(summary.total_income)}</div>
          </div>
          <div className="rounded-xl bg-rose-50 border border-rose-200 p-4">
            <div className="text-[10px] uppercase tracking-wide text-rose-700 font-bold flex items-center gap-1"><ArrowUpCircle size={12}/> Total Expense</div>
            <div className="font-display text-xl font-bold text-rose-800 mt-1">{inr(summary.total_expense)}</div>
          </div>
          <div className={`rounded-xl p-4 border ${summary.profit ? "bg-kp-teal-light border-kp-teal/30" : "bg-amber-50 border-amber-200"}`}>
            <div className={`text-[10px] uppercase tracking-wide font-bold flex items-center gap-1 ${summary.profit ? "text-kp-teal" : "text-amber-700"}`}><TrendingUp size={12}/> {summary.profit ? "Net Profit" : "Net Loss"}</div>
            <div className={`font-display text-xl font-bold mt-1 ${summary.profit ? "text-kp-teal" : "text-amber-800"}`}>{inr(Math.abs(summary.net))}</div>
          </div>
          <div className="rounded-xl bg-blue-50 border border-blue-200 p-4">
            <div className="text-[10px] uppercase tracking-wide text-blue-700 font-bold">Receivable (Unpaid Income)</div>
            <div className="font-display text-xl font-bold text-blue-800 mt-1">{inr(summary.outstanding_receivable)}</div>
          </div>
          <div className="rounded-xl bg-orange-50 border border-orange-200 p-4">
            <div className="text-[10px] uppercase tracking-wide text-orange-700 font-bold">Payable (Unpaid Expense)</div>
            <div className="font-display text-xl font-bold text-orange-800 mt-1">{inr(summary.outstanding_payable)}</div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-end bg-slate-50 rounded-xl p-3 border">
        <div><label className="text-[10px] font-bold text-slate-500 uppercase">Type</label>
          <Select value={filter.type} onValueChange={(v) => setFilter({ ...filter, type: v })}>
            <SelectTrigger className="w-32"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="income">Income</SelectItem><SelectItem value="expense">Expense</SelectItem></SelectContent>
          </Select>
        </div>
        <div><label className="text-[10px] font-bold text-slate-500 uppercase">Status</label>
          <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
            <SelectTrigger className="w-32"><SelectValue/></SelectTrigger>
            <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="paid">Paid</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem></SelectContent>
          </Select>
        </div>
        <div><label className="text-[10px] font-bold text-slate-500 uppercase">From</label><Input type="date" value={filter.from} onChange={(e) => setFilter({ ...filter, from: e.target.value })} className="w-40"/></div>
        <div><label className="text-[10px] font-bold text-slate-500 uppercase">To</label><Input type="date" value={filter.to} onChange={(e) => setFilter({ ...filter, to: e.target.value })} className="w-40"/></div>
        {(filter.from || filter.to || filter.type !== "all" || filter.status !== "all") && (
          <Button size="sm" variant="ghost" onClick={() => setFilter({ type: "all", status: "all", from: "", to: "" })}>Clear filters</Button>
        )}
      </div>

      {list.length === 0 ? (
        <div className="text-center py-10 text-slate-500 border border-dashed rounded-xl">No entries. Add your first income or expense to get started.</div>
      ) : (
        <div className="overflow-x-auto border rounded-xl">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs text-slate-600 uppercase">
              <tr><th className="p-2">Date</th><th className="p-2">Particular</th><th className="p-2">Mode</th><th className="p-2">Type</th><th className="p-3 text-right">Amount</th><th className="p-2">Status</th><th className="p-2"></th></tr>
            </thead>
            <tbody>
              {list.map((e) => (
                <tr key={e.id} className="border-t hover:bg-slate-50">
                  <td className="p-3 text-xs whitespace-nowrap">{new Date(e.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}</td>
                  <td className="p-2">
                    <div className="font-medium text-slate-800 flex items-center gap-1.5">
                      {e.particular}
                      {e.auto && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded bg-kp-teal-light text-kp-teal uppercase tracking-wide" data-testid={`ledger-auto-${e.id}`} title={e.invoice_no ? `Auto-posted from ${e.invoice_no}` : "Auto-posted from a closed deal"}>
                          <Handshake size={10}/> Auto
                        </span>
                      )}
                    </div>
                    {e.notes && <div className="text-xs text-slate-500 mt-0.5">{e.notes}</div>}
                  </td>
                  <td className="p-3 text-xs">{modeLabel[e.mode] || e.mode}</td>
                  <td className="p-2">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded ${e.entry_type === "income" ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                      {e.entry_type === "income" ? "Income" : "Expense"}
                    </span>
                  </td>
                  <td className={`p-3 text-right font-bold ${e.entry_type === "income" ? "text-emerald-700" : "text-rose-700"}`}>
                    {e.entry_type === "income" ? "+" : "-"} {inr(e.amount)}
                  </td>
                  <td className="p-2">
                    <button onClick={() => togglePaid(e)} className={`text-xs font-semibold px-2 py-0.5 rounded ${e.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid={`ledger-toggle-${e.id}`}>
                      {e.status === "paid" ? "Paid" : "Unpaid"}
                    </button>
                  </td>
                  <td className="p-2">
                    <div className="flex gap-1">
                      <button onClick={() => setEditing(e)} className="text-kp-teal hover:bg-kp-teal-light p-1.5 rounded" data-testid={`ledger-edit-${e.id}`}><Edit size={14}/></button>
                      <button onClick={() => del(e)} className="text-red-600 hover:bg-red-50 p-1.5 rounded" data-testid={`ledger-delete-${e.id}`}><Trash2 size={14}/></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
            {summary && list.length > 0 && (
              <tfoot className="bg-slate-100 font-bold border-t-2 border-slate-300">
                <tr>
                  <td colSpan={4} className="p-3 text-right text-slate-700">Grand Total (Paid only):</td>
                  <td className={`p-3 text-right ${summary.profit ? "text-emerald-700" : "text-rose-700"}`}>{summary.profit ? "+" : "-"} {inr(Math.abs(summary.net))}</td>
                  <td colSpan={2} className="p-3 text-xs text-slate-500 font-normal">{summary.profit ? "Net Profit" : "Net Loss"}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
      {editing && <LedgerDialog entry={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function LedgerDialog({ entry, onClose, onSaved }) {
  const isEdit = !!entry.id;
  const [f, setF] = useState({
    date: entry.date || new Date().toISOString().slice(0, 10),
    particular: entry.particular || "",
    mode: entry.mode || "cash",
    entry_type: entry.entry_type || "expense",
    amount: entry.amount || "",
    status: entry.status || "paid",
    notes: entry.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    const amt = parseFloat(f.amount);
    if (!f.particular.trim()) { toast.error("Particular required"); return; }
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setSaving(true);
    try {
      const payload = { ...f, amount: amt };
      if (isEdit) await api.patch(`/admin/ledger/${entry.id}`, payload);
      else await api.post("/admin/ledger", payload);
      toast.success(isEdit ? "Entry updated" : "Entry added");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="ledger-dialog">
        <DialogTitle className="font-display text-xl flex items-center gap-2">
          <BookMarked size={20} className="text-kp-teal"/> {isEdit ? "Edit Entry" : "New Account Entry"}
        </DialogTitle>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" onClick={() => set("entry_type", "income")} className={`p-3 rounded-xl border font-semibold flex items-center justify-center gap-1 ${f.entry_type === "income" ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-slate-500"}`} data-testid="ledger-type-income"><ArrowDownCircle size={16}/> Income</button>
            <button type="button" onClick={() => set("entry_type", "expense")} className={`p-3 rounded-xl border font-semibold flex items-center justify-center gap-1 ${f.entry_type === "expense" ? "border-rose-500 bg-rose-50 text-rose-700" : "border-slate-200 text-slate-500"}`} data-testid="ledger-type-expense"><ArrowUpCircle size={16}/> Expense</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600">Date *</label><Input type="date" value={f.date} onChange={(e) => set("date", e.target.value)} data-testid="ledger-date"/></div>
            <div><label className="text-xs font-semibold text-slate-600">Amount (₹) *</label><Input type="number" min="1" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="ledger-amount"/></div>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">Particular *</label><Input value={f.particular} onChange={(e) => set("particular", e.target.value)} placeholder={f.entry_type === "income" ? "e.g. Commission from Vesu deal" : "e.g. Office rent, Ad spend"} data-testid="ledger-particular"/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs font-semibold text-slate-600">Mode of Payment</label>
              <Select value={f.mode} onValueChange={(v) => set("mode", v)}>
                <SelectTrigger data-testid="ledger-mode"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem><SelectItem value="bank">Bank Transfer</SelectItem><SelectItem value="cheque">Cheque</SelectItem><SelectItem value="card">Card</SelectItem></SelectContent>
              </Select>
            </div>
            <div><label className="text-xs font-semibold text-slate-600">Status</label>
              <Select value={f.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="ledger-status"><SelectValue/></SelectTrigger>
                <SelectContent><SelectItem value="paid">Paid</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div><label className="text-xs font-semibold text-slate-600">Notes (optional)</label><Textarea rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)}/></div>
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={save} disabled={saving} className="btn-primary flex-1 font-semibold" data-testid="ledger-save">{saving ? "Saving..." : (isEdit ? "Update Entry" : "Add Entry")}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function BroadcastPanel() {
  const [f, setF] = useState({ audience: "leads", locality: "", lead_status: "", custom_phones: "", message: "" });
  const [count, setCount] = useState(null);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const loadHistory = () => api.get("/admin/broadcasts").then((r) => setHistory(r.data));
  useEffect(() => { loadHistory(); }, []);

  useEffect(() => {
    if (f.audience === "custom") {
      const phones = f.custom_phones.split(/[,\n\s]+/).filter((p) => p.trim().length >= 8);
      setCount(phones.length);
      return;
    }
    api.get("/admin/broadcasts/audience-count", {
      params: { audience: f.audience, locality: f.locality, lead_status: f.lead_status }
    }).then((r) => setCount(r.data.count)).catch(() => setCount(null));
  }, [f.audience, f.locality, f.lead_status, f.custom_phones]);

  const send = async () => {
    if (!f.message.trim()) { toast.error("Message is required"); return; }
    if (f.message.length > 1500) { toast.error("Message too long"); return; }
    if (!count || count === 0) { toast.error("No recipients match"); return; }
    if (!window.confirm(`Send WhatsApp broadcast to ${count} recipient(s)?`)) return;
    setSending(true);
    try {
      const payload = { audience: f.audience, locality: f.locality, lead_status: f.lead_status, message: f.message };
      if (f.audience === "custom") payload.custom_phones = f.custom_phones.split(/[,\n\s]+/).map((p) => p.trim()).filter(Boolean);
      const r = await api.post("/admin/broadcasts", payload);
      const b = r.data.broadcast;
      if (b.sent > 0 && b.failed === 0) toast.success(`Delivered to ${b.sent} recipient(s)`);
      else if (b.sent > 0) toast.success(`Sent ${b.sent}, ${b.failed} failed`);
      else toast.error(`All ${b.failed} failed — check errors below`);
      setF((s) => ({ ...s, message: "" }));
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || "Broadcast failed"); } finally { setSending(false); }
  };

  return (
    <div className="p-4 md:p-5 space-y-4" data-testid="admin-broadcast-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2"><MessageCircle size={18} className="text-emerald-600"/> WhatsApp Broadcast</h3>
          <p className="text-xs text-slate-500 mt-1">Send WhatsApp messages to leads, users, or a custom phone list via Twilio.</p>
        </div>
        <ExportButton kind="broadcasts"/>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-900 flex gap-2">
        <AlertCircle size={16} className="shrink-0 mt-0.5"/>
        <div>
          <b>Twilio Sandbox mode:</b> Recipients must first join the sandbox by sending <b>join &lt;keyword&gt;</b> to <b>+1 415 523 8886</b> on WhatsApp. To send to any number without opt-in, register a WhatsApp Business Sender in Twilio and set <code>TWILIO_WHATSAPP_FROM</code> to your approved number.
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr,1fr]">
        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <h4 className="font-semibold text-sm text-slate-700">1. Choose audience</h4>
          <div className="grid grid-cols-3 gap-2">
            {[
              ["leads", "CRM Leads", UserPlus],
              ["users", "All Users", Users],
              ["custom", "Custom List", Send],
            ].map(([k, label, Icon]) => (
              <button key={k} onClick={() => set("audience", k)} data-testid={`audience-${k}`}
                className={`p-3 rounded-xl border font-semibold text-xs flex flex-col items-center gap-1 ${f.audience === k ? "border-kp-teal bg-kp-teal-light text-kp-teal" : "border-slate-200 text-slate-600 hover:border-kp-teal"}`}>
                <Icon size={16}/> {label}
              </button>
            ))}
          </div>

          {f.audience === "leads" && (
            <>
              <div>
                <label className="text-xs font-semibold text-slate-600">Filter by Locality (optional)</label>
                <Select value={f.locality || "any"} onValueChange={(v) => set("locality", v === "any" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="All localities"/></SelectTrigger>
                  <SelectContent className="max-h-72"><SelectItem value="any">All localities</SelectItem>{LOCALITIES.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600">Filter by Status (optional)</label>
                <Select value={f.lead_status || "any"} onValueChange={(v) => set("lead_status", v === "any" ? "" : v)}>
                  <SelectTrigger><SelectValue placeholder="Any status"/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any status</SelectItem>
                    <SelectItem value="new">New</SelectItem><SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="site_visit">Site Visit</SelectItem><SelectItem value="negotiating">Negotiating</SelectItem>
                    <SelectItem value="won">Won</SelectItem><SelectItem value="lost">Lost</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {f.audience === "custom" && (
            <div>
              <label className="text-xs font-semibold text-slate-600">Phone numbers (comma or newline separated)</label>
              <Textarea rows={4} value={f.custom_phones} onChange={(e) => set("custom_phones", e.target.value)} placeholder={"9876543210\n9812345678\n+919999999999"} className="font-mono text-xs" data-testid="broadcast-phones"/>
            </div>
          )}

          <div className="rounded-xl bg-slate-50 p-3 text-sm flex items-center justify-between">
            <span className="text-slate-600">Recipients matched:</span>
            <span className="font-bold text-kp-teal text-lg" data-testid="audience-count">{count === null ? "…" : count}</span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-3">
          <h4 className="font-semibold text-sm text-slate-700">2. Compose message</h4>
          <Textarea rows={8} value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Type your WhatsApp message here...&#10;&#10;Tip: You can use *bold*, _italic_ and emojis. Keep it under 1500 characters." data-testid="broadcast-message"/>
          <div className="flex items-center justify-between text-xs text-slate-500">
            <span>{f.message.length} / 1500 characters</span>
            <Button onClick={send} disabled={sending || !count} className="btn-primary rounded-full gap-1 font-semibold" data-testid="broadcast-send">
              <Send size={14}/> {sending ? "Sending..." : `Send to ${count || 0}`}
            </Button>
          </div>
        </div>
      </div>

      <div>
        <h4 className="font-semibold text-sm text-slate-700 mb-2">Broadcast History</h4>
        {history.length === 0 ? (
          <div className="text-center py-6 text-slate-500 border border-dashed rounded-xl text-sm">No broadcasts sent yet.</div>
        ) : (
          <div className="overflow-x-auto border rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs text-slate-600 uppercase">
                <tr><th className="p-2">Date</th><th className="p-2">Audience</th><th className="p-2">Message</th><th className="p-2 text-right">Recipients</th><th className="p-2 text-right">Sent</th><th className="p-2 text-right">Failed</th></tr>
              </thead>
              <tbody>
                {history.map((b) => (
                  <tr key={b.id} className="border-t hover:bg-slate-50">
                    <td className="p-2 text-xs whitespace-nowrap">{new Date(b.created_at).toLocaleString("en-IN", {day:"2-digit",month:"short",hour:"2-digit",minute:"2-digit"})}</td>
                    <td className="p-2 text-xs capitalize">{b.audience}{b.locality ? ` · ${b.locality}` : ""}{b.lead_status ? ` · ${b.lead_status}` : ""}</td>
                    <td className="p-2 max-w-md truncate text-slate-700">{b.message}</td>
                    <td className="p-2 text-right">{b.recipient_count}</td>
                    <td className="p-2 text-right"><span className="flex items-center gap-1 justify-end text-emerald-700"><CheckCircle2 size={12}/> {b.sent}</span></td>
                    <td className="p-2 text-right">{b.failed > 0 ? <span className="text-rose-700 font-semibold" title={(b.errors || []).join('\n')}>{b.failed}</span> : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function EditPropertyDialog({ property, onClose, onSaved }) {
  const [f, setF] = useState({
    title: property.title || "", description: property.description || "",
    purpose: property.purpose, category: property.category, property_type: property.property_type || "",
    price: property.price || 0, price_label: property.price_label || "",
    locality: property.locality || "", address: property.address || "",
    bedrooms: property.bedrooms ?? "", bathrooms: property.bathrooms ?? "", area: property.area || 0,
    verified: property.verified, featured: property.featured, status: property.status,
    owner_name: property.owner_name || "", owner_phone: property.owner_phone || "", owner_email: property.owner_email || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...f, price: parseFloat(f.price) || 0, area: parseFloat(f.area) || 0,
        bedrooms: f.bedrooms === "" ? null : parseInt(f.bedrooms), bathrooms: f.bathrooms === "" ? null : parseInt(f.bathrooms) };
      await api.patch(`/admin/properties/${property.id}`, payload);
      toast.success("Property updated");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); } finally { setSaving(false); }
  };
  const types = PROPERTY_TYPES[f.category] || [];
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogTitle className="font-display text-xl">Edit Property</DialogTitle>
        <div className="space-y-3 mt-2" data-testid="admin-edit-dialog">
          <div><label className="text-xs font-semibold text-slate-600">Title</label><Input value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="edit-title"/></div>
          <div><label className="text-xs font-semibold text-slate-600">Description</label><Textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)}/></div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="text-xs text-slate-600">Purpose</label>
              <Select value={f.purpose} onValueChange={(v) => set("purpose", v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="sale">Sale</SelectItem><SelectItem value="rent">Rent</SelectItem><SelectItem value="lease">Lease</SelectItem></SelectContent></Select>
            </div>
            <div><label className="text-xs text-slate-600">Category</label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="residential">Residential</SelectItem><SelectItem value="commercial">Commercial</SelectItem><SelectItem value="industrial">Industrial</SelectItem><SelectItem value="warehouse">Warehouse</SelectItem><SelectItem value="plot">Plot</SelectItem></SelectContent></Select>
            </div>
            <div><label className="text-xs text-slate-600">Type</label>
              <Select value={f.property_type} onValueChange={(v) => set("property_type", v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent>{types.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-slate-600">Price (₹)</label><Input type="number" value={f.price} onChange={(e) => set("price", e.target.value)} data-testid="edit-price"/></div>
            <div><label className="text-xs text-slate-600">Price Label</label><Input value={f.price_label} onChange={(e) => set("price_label", e.target.value)}/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-slate-600">Locality</label>
              <Select value={f.locality} onValueChange={(v) => set("locality", v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent className="max-h-72">{LOCALITIES.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent></Select>
            </div>
            <div><label className="text-xs text-slate-600">Address</label><Input value={f.address} onChange={(e) => set("address", e.target.value)}/></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="text-xs text-slate-600">Area</label><Input type="number" value={f.area} onChange={(e) => set("area", e.target.value)}/></div>
            <div><label className="text-xs text-slate-600">Bedrooms</label><Input type="number" value={f.bedrooms} onChange={(e) => set("bedrooms", e.target.value)}/></div>
            <div><label className="text-xs text-slate-600">Bathrooms</label><Input type="number" value={f.bathrooms} onChange={(e) => set("bathrooms", e.target.value)}/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="text-xs text-slate-600">Owner Name</label><Input value={f.owner_name} onChange={(e) => set("owner_name", e.target.value)}/></div>
            <div><label className="text-xs text-slate-600">Owner Phone</label><Input value={f.owner_phone} onChange={(e) => set("owner_phone", e.target.value)}/></div>
          </div>
          <div><label className="text-xs text-slate-600">Owner Email</label><Input type="email" value={f.owner_email} onChange={(e) => set("owner_email", e.target.value)} placeholder="Auto-invoiced on deal close"/></div>
          <div className="grid grid-cols-3 gap-2 pt-2 border-t">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.verified} onChange={(e) => set("verified", e.target.checked)} data-testid="edit-verified"/> Verified</label>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured", e.target.checked)}/> Featured</label>
            <div>
              <Select value={f.status} onValueChange={(v) => set("status", v)}><SelectTrigger><SelectValue/></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="pending">Pending</SelectItem><SelectItem value="sold">Sold</SelectItem></SelectContent></Select>
            </div>
          </div>
          <div className="flex gap-2 pt-3">
            <Button variant="outline" onClick={onClose} className="flex-1">Cancel</Button>
            <Button onClick={save} disabled={saving} className="btn-primary flex-1 font-semibold" data-testid="edit-save">{saving ? "Saving..." : "Save & Verify"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
